// src/enums.ts
var USER_ROLES = ["SUPER_ADMIN", "ADMIN"];
var USER_STATUSES = ["INVITED", "ACTIVE", "DISABLED"];
var OBJECT_TYPES = ["CONTACT", "ORGANIZATION", "DEAL"];
var FIELD_TYPES = [
  "TEXT",
  "TEXTAREA",
  "NUMBER",
  "CURRENCY",
  "DATE",
  "DATETIME",
  "BOOLEAN",
  "SELECT",
  "MULTISELECT",
  "EMAIL",
  "PHONE",
  "URL",
  /** a list of links, each with a label: [{ url, label }] */
  "LINKS",
  /** a list of free-form text values: string[] */
  "LIST"
];
var EMAIL_STATUSES = [
  "UNVERIFIED",
  "VALID",
  "INVALID",
  "CATCH_ALL",
  "DISPOSABLE",
  "UNKNOWN",
  "SUPPRESSED"
];
var ACTIVITY_TYPES = [
  "NOTE",
  "CALL",
  "MEETING",
  "TASK_CREATED",
  "TASK_DONE",
  "EMAIL_SENT",
  "EMAIL_RECEIVED",
  "EMAIL_DELIVERED",
  "EMAIL_OPENED",
  "EMAIL_CLICKED",
  "EMAIL_BOUNCED",
  "EMAIL_COMPLAINED",
  "UNSUBSCRIBED",
  "TAG_ADDED",
  "TAG_REMOVED",
  "LIST_ADDED",
  "LIST_REMOVED",
  "SEGMENT_ENTERED",
  "SEGMENT_LEFT",
  "STAGE_CHANGED",
  "DEAL_CREATED",
  "DEAL_WON",
  "DEAL_LOST",
  "FIELD_CHANGED",
  "CONTACT_CREATED",
  "ORGANIZATION_CHANGED",
  "FORM_SUBMITTED",
  "IMPORTED",
  "VERIFIED",
  "MERGED",
  "UNMERGED"
];

// src/auth.ts
import { z } from "zod";
var registerSchema = z.object({
  name: z.string().trim().min(1).max(120),
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(10).max(200)
});
var loginSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  password: z.string().min(1)
});
var acceptInviteSchema = z.object({
  token: z.string().min(10),
  name: z.string().trim().min(1).max(120),
  password: z.string().min(10).max(200)
});
var forgotPasswordSchema = z.object({
  email: z.string().trim().toLowerCase().email()
});
var resetPasswordSchema = z.object({
  token: z.string().min(10),
  password: z.string().min(10).max(200)
});
var inviteUserSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  name: z.string().trim().min(1).max(120),
  role: z.enum(["SUPER_ADMIN", "ADMIN"]).default("ADMIN")
});
var updateUserSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  role: z.enum(["SUPER_ADMIN", "ADMIN"]).optional(),
  status: z.enum(["ACTIVE", "DISABLED"]).optional()
});
var updateProfileSchema = z.object({
  name: z.string().trim().min(1).max(120)
});
var changePasswordSchema = z.object({
  currentPassword: z.string().min(1),
  newPassword: z.string().min(10).max(200)
});

// src/fields.ts
import { z as z2 } from "zod";
var fieldOptionSchema = z2.object({
  value: z2.string().min(1),
  label: z2.string().min(1),
  color: z2.string().optional()
});
var fieldKeyRegex = /^[a-z][a-z0-9_]{0,63}$/;
var createFieldSchema = z2.object({
  objectType: z2.enum(OBJECT_TYPES),
  key: z2.string().regex(fieldKeyRegex, "snake_case, letters/numbers/underscore"),
  label: z2.string().trim().min(1).max(120),
  type: z2.enum(FIELD_TYPES),
  options: z2.array(fieldOptionSchema).optional(),
  required: z2.boolean().default(false),
  showInTable: z2.boolean().default(false),
  moreInfo: z2.boolean().default(false),
  /** shown on the quick create modal (required fields always are) */
  quickAdd: z2.boolean().default(false),
  group: z2.string().trim().max(80).optional(),
  description: z2.string().trim().max(500).optional(),
  sortOrder: z2.number().int().optional()
});
var updateFieldSchema = createFieldSchema.omit({ objectType: true, key: true, type: true }).extend({ required: z2.boolean(), showInTable: z2.boolean(), moreInfo: z2.boolean(), quickAdd: z2.boolean() }).partial();
var reorderFieldsSchema = z2.object({
  objectType: z2.enum(OBJECT_TYPES),
  ids: z2.array(z2.string()).min(1)
});
var LIST_FIELD_TYPES = ["MULTISELECT", "LINKS", "LIST"];
var mergeLabel = z2.string().trim().max(60).optional();
var mergeFieldSchema = z2.discriminatedUnion("into", [
  z2.object({ into: z2.literal("emails"), label: mergeLabel }),
  z2.object({ into: z2.literal("phones"), label: mergeLabel }),
  /** `label` is used when the target is a links list */
  z2.object({ into: z2.literal("field"), targetFieldId: z2.string().min(1), label: mergeLabel }),
  /** contact field → the field on each contact's organization; no target creates one like the source */
  z2.object({ into: z2.literal("organization"), targetFieldId: z2.string().min(1).optional(), label: mergeLabel })
]);
var MERGEABLE_SYSTEM_KEYS = {
  CONTACT: ["first_name", "last_name", "job_title", "lifecycle_stage", "lead_status", "source", "created_at"],
  ORGANIZATION: ["name", "domain", "website", "industry", "type", "employees", "annual_revenue", "phone", "created_at"],
  DEAL: ["title", "value", "currency", "expected_close_at", "source", "description", "lost_reason", "created_at"]
};
var changeFieldTypeSchema = z2.object({
  type: z2.enum(FIELD_TYPES),
  /** text and long text: what goes between list values (default a space, a new line for long text) */
  separator: z2.string().max(10).optional(),
  dryRun: z2.boolean().default(false)
});

// src/rules.ts
import { z as z3 } from "zod";
var RULE_OPERATORS = [
  "eq",
  "neq",
  "contains",
  "not_contains",
  "starts_with",
  "is_empty",
  "is_not_empty",
  "gt",
  "gte",
  "lt",
  "lte",
  "in",
  "not_in",
  "before",
  "after",
  "within_days",
  "not_within_days"
];
var scalar = z3.union([z3.string(), z3.number(), z3.boolean(), z3.null()]);
var fieldConditionSchema = z3.object({
  kind: z3.literal("field"),
  /** system column key or custom field key */
  field: z3.string().min(1),
  op: z3.enum(RULE_OPERATORS),
  value: z3.union([scalar, z3.array(scalar)]).optional()
});
var tagConditionSchema = z3.object({
  kind: z3.literal("tag"),
  op: z3.enum(["any_of", "all_of", "none_of"]),
  tagIds: z3.array(z3.string()).min(1)
});
var listConditionSchema = z3.object({
  kind: z3.literal("list"),
  op: z3.enum(["in_any", "not_in_any"]),
  listIds: z3.array(z3.string()).min(1)
});
var segmentConditionSchema = z3.object({
  kind: z3.literal("segment"),
  op: z3.enum(["in_any", "not_in_any"]),
  segmentIds: z3.array(z3.string()).min(1)
});
var organizationConditionSchema = z3.object({
  kind: z3.literal("organization"),
  field: z3.string().min(1),
  op: z3.enum(RULE_OPERATORS),
  value: z3.union([scalar, z3.array(scalar)]).optional()
});
var dealConditionSchema = z3.object({
  kind: z3.literal("deal"),
  op: z3.enum(["has_deal_in_stage", "has_no_deal_in_stage", "has_any_deal", "has_no_deal"]),
  pipelineId: z3.string().optional(),
  stageIds: z3.array(z3.string()).optional()
});
var engagementConditionSchema = z3.object({
  kind: z3.literal("engagement"),
  event: z3.enum(["opened", "clicked", "received_email", "replied", "bounced"]),
  op: z3.enum(["within_days", "not_within_days", "ever", "never"]),
  days: z3.number().int().positive().optional(),
  campaignId: z3.string().optional()
});
var projectConditionSchema = z3.object({
  kind: z3.literal("project"),
  projectId: z3.string().min(1)
});
var conditionSchema = z3.discriminatedUnion("kind", [
  fieldConditionSchema,
  tagConditionSchema,
  listConditionSchema,
  segmentConditionSchema,
  organizationConditionSchema,
  dealConditionSchema,
  engagementConditionSchema,
  projectConditionSchema
]);
var ruleGroupSchema = z3.lazy(
  () => z3.object({
    combinator: z3.enum(["and", "or"]),
    conditions: z3.array(z3.union([conditionSchema, ruleGroupSchema]))
  })
);
var emptyRuleGroup = () => ({ combinator: "and", conditions: [] });

// src/pagination.ts
import { z as z4 } from "zod";
var paginationSchema = z4.object({
  page: z4.coerce.number().int().min(1).default(1),
  pageSize: z4.coerce.number().int().min(1).max(200).default(25),
  sortBy: z4.string().optional(),
  sortDir: z4.enum(["asc", "desc"]).optional(),
  q: z4.string().trim().optional()
});

// src/contacts.ts
import { z as z6 } from "zod";

// src/addresses.ts
import { z as z5 } from "zod";

// src/countries.ts
var COUNTRY_ROWS = [
  "AF|Afghanistan",
  "AX|\xC5land Islands|Aland Islands",
  "AL|Albania",
  "DZ|Algeria",
  "AS|American Samoa",
  "AD|Andorra",
  "AO|Angola",
  "AI|Anguilla",
  "AQ|Antarctica",
  "AG|Antigua and Barbuda|Antigua",
  "AR|Argentina",
  "AM|Armenia",
  "AW|Aruba",
  "AU|Australia",
  "AT|Austria|\xD6sterreich|Osterreich",
  "AZ|Azerbaijan",
  "BS|Bahamas",
  "BH|Bahrain",
  "BD|Bangladesh",
  "BB|Barbados",
  "BY|Belarus",
  "BE|Belgium|Belgique|Belgi\xEB",
  "BZ|Belize",
  "BJ|Benin",
  "BM|Bermuda",
  "BT|Bhutan",
  "BO|Bolivia",
  "BA|Bosnia and Herzegovina|Bosnia",
  "BW|Botswana",
  "BR|Brazil|Brasil",
  "IO|British Indian Ocean Territory",
  "VG|British Virgin Islands",
  "BN|Brunei",
  "BG|Bulgaria",
  "BF|Burkina Faso",
  "BI|Burundi",
  "KH|Cambodia",
  "CM|Cameroon",
  "CA|Canada",
  "CV|Cape Verde|Cabo Verde",
  "KY|Cayman Islands",
  "CF|Central African Republic",
  "TD|Chad",
  "CL|Chile",
  "CN|China|People's Republic of China|PRC",
  "CX|Christmas Island",
  "CC|Cocos Islands",
  "CO|Colombia",
  "KM|Comoros",
  "CG|Congo|Republic of the Congo",
  "CD|Democratic Republic of the Congo|DR Congo|DRC",
  "CK|Cook Islands",
  "CR|Costa Rica",
  "CI|C\xF4te d'Ivoire|Cote d'Ivoire|Ivory Coast",
  "HR|Croatia|Hrvatska",
  "CU|Cuba",
  "CW|Cura\xE7ao|Curacao",
  "CY|Cyprus|\u039A\u03CD\u03C0\u03C1\u03BF\u03C2|Kypros|Republic of Cyprus",
  "CZ|Czechia|Czech Republic",
  "DK|Denmark|Danmark",
  "DJ|Djibouti",
  "DM|Dominica",
  "DO|Dominican Republic",
  "EC|Ecuador",
  "EG|Egypt",
  "SV|El Salvador",
  "GQ|Equatorial Guinea",
  "ER|Eritrea",
  "EE|Estonia",
  "SZ|Eswatini|Swaziland",
  "ET|Ethiopia",
  "FK|Falkland Islands",
  "FO|Faroe Islands",
  "FJ|Fiji",
  "FI|Finland|Suomi",
  "FR|France",
  "GF|French Guiana",
  "PF|French Polynesia",
  "GA|Gabon",
  "GM|Gambia",
  "GE|Georgia",
  "DE|Germany|Deutschland",
  "GH|Ghana",
  "GI|Gibraltar",
  "GR|Greece|\u0395\u03BB\u03BB\u03AC\u03B4\u03B1|Ellada|Hellas",
  "GL|Greenland",
  "GD|Grenada",
  "GP|Guadeloupe",
  "GU|Guam",
  "GT|Guatemala",
  "GG|Guernsey",
  "GN|Guinea",
  "GW|Guinea-Bissau",
  "GY|Guyana",
  "HT|Haiti",
  "HN|Honduras",
  "HK|Hong Kong",
  "HU|Hungary|Magyarorsz\xE1g",
  "IS|Iceland",
  "IN|India",
  "ID|Indonesia",
  "IR|Iran",
  "IQ|Iraq",
  "IE|Ireland|Republic of Ireland|\xC9ire",
  "IM|Isle of Man",
  "IL|Israel",
  "IT|Italy|Italia",
  "JM|Jamaica",
  "JP|Japan",
  "JE|Jersey",
  "JO|Jordan",
  "KZ|Kazakhstan",
  "KE|Kenya",
  "KI|Kiribati",
  "XK|Kosovo",
  "KW|Kuwait",
  "KG|Kyrgyzstan",
  "LA|Laos",
  "LV|Latvia",
  "LB|Lebanon",
  "LS|Lesotho",
  "LR|Liberia",
  "LY|Libya",
  "LI|Liechtenstein",
  "LT|Lithuania",
  "LU|Luxembourg",
  "MO|Macao|Macau",
  "MG|Madagascar",
  "MW|Malawi",
  "MY|Malaysia",
  "MV|Maldives",
  "ML|Mali",
  "MT|Malta",
  "MH|Marshall Islands",
  "MQ|Martinique",
  "MR|Mauritania",
  "MU|Mauritius",
  "YT|Mayotte",
  "MX|Mexico|M\xE9xico",
  "FM|Micronesia",
  "MD|Moldova",
  "MC|Monaco",
  "MN|Mongolia",
  "ME|Montenegro",
  "MS|Montserrat",
  "MA|Morocco",
  "MZ|Mozambique",
  "MM|Myanmar|Burma",
  "NA|Namibia",
  "NR|Nauru",
  "NP|Nepal",
  "NL|Netherlands|The Netherlands|Holland|Nederland",
  "NC|New Caledonia",
  "NZ|New Zealand",
  "NI|Nicaragua",
  "NE|Niger",
  "NG|Nigeria",
  "NU|Niue",
  "NF|Norfolk Island",
  "KP|North Korea",
  "MK|North Macedonia|Macedonia",
  "MP|Northern Mariana Islands",
  "NO|Norway|Norge",
  "OM|Oman",
  "PK|Pakistan",
  "PW|Palau",
  "PS|Palestine",
  "PA|Panama",
  "PG|Papua New Guinea",
  "PY|Paraguay",
  "PE|Peru",
  "PH|Philippines",
  "PN|Pitcairn Islands",
  "PL|Poland|Polska",
  "PT|Portugal",
  "PR|Puerto Rico",
  "QA|Qatar",
  "RE|R\xE9union|Reunion",
  "RO|Romania",
  "RU|Russia|Russian Federation",
  "RW|Rwanda",
  "BL|Saint Barth\xE9lemy|Saint Barthelemy",
  "SH|Saint Helena",
  "KN|Saint Kitts and Nevis",
  "LC|Saint Lucia",
  "MF|Saint Martin",
  "PM|Saint Pierre and Miquelon",
  "VC|Saint Vincent and the Grenadines",
  "WS|Samoa",
  "SM|San Marino",
  "ST|S\xE3o Tom\xE9 and Pr\xEDncipe|Sao Tome and Principe",
  "SA|Saudi Arabia|KSA",
  "SN|Senegal",
  "RS|Serbia",
  "SC|Seychelles",
  "SL|Sierra Leone",
  "SG|Singapore",
  "SX|Sint Maarten",
  "SK|Slovakia",
  "SI|Slovenia",
  "SB|Solomon Islands",
  "SO|Somalia",
  "ZA|South Africa|RSA",
  "KR|South Korea|Korea|Republic of Korea",
  "SS|South Sudan",
  "ES|Spain|Espa\xF1a|Espana",
  "LK|Sri Lanka",
  "SD|Sudan",
  "SR|Suriname",
  "SE|Sweden|Sverige",
  "CH|Switzerland|Schweiz|Suisse",
  "SY|Syria",
  "TW|Taiwan",
  "TJ|Tajikistan",
  "TZ|Tanzania",
  "TH|Thailand",
  "TL|Timor-Leste|East Timor",
  "TG|Togo",
  "TK|Tokelau",
  "TO|Tonga",
  "TT|Trinidad and Tobago",
  "TN|Tunisia",
  "TR|Turkey|T\xFCrkiye|Turkiye",
  "TM|Turkmenistan",
  "TC|Turks and Caicos Islands",
  "TV|Tuvalu",
  "UG|Uganda",
  "UA|Ukraine",
  "AE|United Arab Emirates|UAE|U.A.E.|Emirates",
  "GB|United Kingdom|UK|U.K.|Great Britain|Britain|England|Scotland|Wales|Northern Ireland",
  "US|United States|USA|U.S.A.|U.S.|United States of America|America",
  "UY|Uruguay",
  "VI|U.S. Virgin Islands|US Virgin Islands",
  "UZ|Uzbekistan",
  "VU|Vanuatu",
  "VA|Vatican City|Holy See",
  "VE|Venezuela",
  "VN|Vietnam|Viet Nam",
  "WF|Wallis and Futuna",
  "EH|Western Sahara",
  "YE|Yemen",
  "ZM|Zambia",
  "ZW|Zimbabwe"
];
var foldText = (s) => s.normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().replace(/[.]/g, "").replace(/[^\p{L}\p{N}' ]+/gu, " ").replace(/\s+/g, " ").trim();
var KEEP_AS_WRITTEN = /* @__PURE__ */ new Set(["england", "scotland", "wales", "northern ireland"]);
var BY_NAME = /* @__PURE__ */ new Map();
var BY_CODE = /* @__PURE__ */ new Map();
var CODE_BY_NAME = /* @__PURE__ */ new Map();
for (const row of COUNTRY_ROWS) {
  const [code, name, ...aliases] = row.split("|");
  BY_CODE.set(code, name);
  for (const n of [name, ...aliases]) {
    BY_NAME.set(foldText(n), KEEP_AS_WRITTEN.has(foldText(n)) ? n : name);
    CODE_BY_NAME.set(foldText(n), code);
  }
}
BY_CODE.set("UK", "United Kingdom");
function matchCountry(value, opts = {}) {
  const v = value.trim();
  if (!v) return null;
  const named = BY_NAME.get(foldText(v).replace(/^the /, ""));
  if (named) return named;
  if (opts.codes && /^[A-Za-z]{2}$/.test(v)) return BY_CODE.get(v.toUpperCase()) ?? null;
  return null;
}
function countryCodeOf(value) {
  const v = value.trim();
  if (!v) return null;
  const named = CODE_BY_NAME.get(foldText(v).replace(/^the /, ""));
  if (named) return named;
  if (/^[A-Za-z]{2}$/.test(v)) {
    const code = v.toUpperCase();
    return code === "UK" ? "GB" : BY_CODE.has(code) ? code : null;
  }
  return null;
}
var REGIONS = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  DC: "District of Columbia",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  NT: "Northwest Territories",
  NU: "Nunavut",
  YT: "Yukon",
  NSW: "New South Wales",
  VIC: "Victoria",
  QLD: "Queensland",
  SA: "South Australia",
  TAS: "Tasmania",
  ACT: "Australian Capital Territory"
};
var REGION_NAMES = new Set([...Object.values(REGIONS), "Western Australia"].map(foldText));
var isKnownRegion = (s) => /^[A-Z]{2,3}$/.test(s.trim()) && s.trim() in REGIONS || REGION_NAMES.has(foldText(s));

// src/addresses.ts
var ADDRESS_PARTS = ["line1", "line2", "city", "state", "country", "postalCode"];
var ADDRESS_PART_LABELS = {
  line1: "Address line 1",
  line2: "Address line 2",
  city: "City",
  state: "State / County / District",
  country: "Country",
  postalCode: "ZIP / Postal code"
};
var ADDRESS_LABELS = ["Work", "Home", "Billing", "Shipping", "Other"];
var part = (max) => z5.string().trim().max(max).nullish().transform((v) => v || null);
var addressSchema = z5.object({
  label: part(40),
  line1: part(300),
  line2: part(300),
  city: part(120),
  state: part(120),
  country: part(120),
  postalCode: part(30)
}).refine((a) => ADDRESS_PARTS.some((p) => a[p]), { message: "An address needs at least one part" });
var addressListSchema = z5.array(addressSchema).max(20);
var clean = (s) => (s ?? "").replace(/\s+/g, " ").replace(/^[\s,;|-]+|[\s,;|]+$/g, "").trim() || null;
function normalizeAddress(a) {
  const country = clean(a.country);
  return {
    label: clean(a.label),
    line1: clean(a.line1),
    line2: clean(a.line2),
    city: clean(a.city),
    state: clean(a.state),
    country: country ? matchCountry(country, { codes: true }) ?? country : null,
    postalCode: clean(a.postalCode)
  };
}
var isEmptyAddress = (a) => ADDRESS_PARTS.every((p) => !clean(a[p]));
var foldPart = (s) => foldText(s ?? "").replace(/'/g, "").replace(/\bstreet\b/g, "st").replace(/\bavenue\b/g, "ave").replace(/\broad\b/g, "rd");
var addressCoveredBy = (a, b) => ADDRESS_PARTS.every((p) => !a[p] || foldPart(a[p] ?? null) === foldPart(b[p] ?? null));
var sameAddress = (a, b) => addressCoveredBy(a, b) && addressCoveredBy(b, a);
function formatAddress(a) {
  const region = [a.state, a.postalCode].filter(Boolean).join(" ");
  return [a.line1, a.line2, a.city, region, a.country].filter(Boolean).join(", ");
}
var describeAddresses = (list) => list.map((a) => (a.label ? `${a.label}: ` : "") + formatAddress(a));
function addAddress(current, incoming) {
  const list = current.map((a) => ({ ...a }));
  const same = list.find((a) => addressCoveredBy(incoming, a));
  if (same) {
    if (!same.label && incoming.label) {
      same.label = incoming.label;
      return { list, changed: true, merged: true };
    }
    return { list, changed: false, merged: true };
  }
  const i = list.findIndex((a) => addressCoveredBy(a, incoming));
  if (i >= 0) list[i] = { ...incoming, label: incoming.label ?? list[i].label };
  else list.push(incoming);
  return { list, changed: true, merged: false };
}
function normalizeAddressList(list) {
  let out = [];
  for (const a of list) if (!isEmptyAddress(a)) out = addAddress(out, normalizeAddress(a)).list;
  return out;
}
var POSTAL_RES = [
  /\b[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}\b/i,
  // UK: SW1A 2AA
  /\b[A-Z]\d[A-Z]\s?\d[A-Z]\d\b/i,
  // Canada: K1A 0B1
  /\b\d{5}-\d{4}\b/,
  // US ZIP+4
  /\b\d{4}\s?[A-Z]{2}\b/,
  // Netherlands: 1012 AB
  /\b\d{3}\s\d{2}\b/,
  // Greece, Sweden, Czechia: 106 71
  /\b\d{2}-\d{3}\b/,
  // Poland: 00-950
  /\b(?:[A-Z]{1,2}-)?\d{4,6}\b/
  // most others, with an optional country prefix: CY-1065, D-10115
];
var NUMBERED_RE = /\b(suite|ste|unit|apt|apartment|floor|fl|box|building|bldg|office|no|nr|room|flat)\b/i;
var STREET_RE = /\b(street|st|road|rd|avenue|ave|av|boulevard|blvd|lane|ln|drive|dr|way|square|sq|str|strasse|straße|odos|leoforos|οδός|λεωφόρος|plaza|place|pl|court|ct|highway|hwy)\b/i;
function takePostal(seg, single) {
  for (const [i, re] of POSTAL_RES.entries()) {
    const generic = i === POSTAL_RES.length - 1;
    if (generic && NUMBERED_RE.test(seg)) continue;
    const all = [...seg.matchAll(new RegExp(re.source, re.flags + "g"))];
    const m = all[all.length - 1];
    if (!m) continue;
    if (single && (m.index === 0 || m.index + m[0].length < seg.trim().length)) continue;
    const rest = (seg.slice(0, m.index) + " " + seg.slice(m.index + m[0].length)).replace(/\s+/g, " ").replace(/^[\s,-]+|[\s,-]+$/g, "");
    return { postal: m[0].trim(), rest };
  }
  return null;
}
function parseAddress(text2) {
  const out = { line1: null, line2: null, city: null, state: null, country: null, postalCode: null };
  const segs = text2.split(/\s*(?:\r?\n|,|;|\|)\s*/).map((s) => clean(s)).filter((s) => !!s);
  if (!segs.length) return out;
  const country = matchCountry(segs[segs.length - 1]);
  if (country) {
    out.country = country;
    segs.pop();
  }
  for (let i = segs.length - 1; i >= Math.max(0, segs.length - 2); i--) {
    if (i === 0 && segs.length > 1) break;
    const hit = takePostal(segs[i], segs.length === 1);
    if (!hit) continue;
    out.postalCode = hit.postal;
    if (hit.rest) segs[i] = hit.rest;
    else segs.splice(i, 1);
    break;
  }
  const extra = [];
  if (out.postalCode && segs.length >= 2) {
    const hit = takePostal(segs[segs.length - 1], false);
    if (hit?.rest && !/\d/.test(hit.rest)) {
      extra.push(out.postalCode);
      out.postalCode = hit.postal;
      segs[segs.length - 1] = hit.rest;
    }
  }
  if (segs.length >= 2 && isKnownRegion(segs[segs.length - 1])) out.state = segs.pop();
  else if (segs.length) {
    const m = segs[segs.length - 1].match(/^(.+?)\s+([A-Z]{2,3})$/);
    if (m && isKnownRegion(m[2])) {
      out.state = m[2];
      segs[segs.length - 1] = m[1];
    }
  }
  if (segs.length >= 2 && !/\d/.test(segs[segs.length - 1]) && !NUMBERED_RE.test(segs[segs.length - 1])) out.city = segs.pop();
  else if (segs.length === 1 && (out.country || out.postalCode || out.state) && !/\d/.test(segs[0]) && !STREET_RE.test(segs[0])) out.city = segs.pop();
  out.line1 = segs[0] ?? null;
  out.line2 = [...segs.slice(1), ...extra].join(", ") || null;
  return out;
}
var ALLOWED_TYPES = /* @__PURE__ */ new Set(["TEXT", "TEXTAREA", "SELECT", "MULTISELECT", "LIST", "NUMBER"]);
var SKIP_WORDS = ["email", "mail", "ip", "web", "website", "url", "link", "mac", "wallet", "phone", "tel", "telephone", "mobile", "fax", "dial", "timezone", "lat", "lng", "latitude", "longitude", "currency", "language", "nationality", "citizenship"];
var QUALIFIERS = {
  billing: "Billing",
  invoice: "Billing",
  invoicing: "Billing",
  shipping: "Shipping",
  delivery: "Shipping",
  mailing: "Mailing",
  postal: "Mailing",
  home: "Home",
  personal: "Home",
  residential: "Home",
  private: "Home",
  work: "Work",
  office: "Work",
  business: "Work",
  company: "Company",
  organization: "Company",
  organisation: "Company",
  org: "Company",
  hq: "HQ",
  headquarters: "HQ",
  registered: "Registered",
  legal: "Registered",
  other: "Other",
  secondary: "Other",
  alt: "Other",
  alternative: "Other"
};
var fieldWords = (key, label) => foldText(`${key} ${label}`.replace(/([a-z])([A-Z])/g, "$1 $2").replace(/([a-z])(\d)/gi, "$1 $2").replace(/_/g, " ")).split(" ");
function classifyAddressField(def) {
  if (!ALLOWED_TYPES.has(def.type)) return null;
  const words2 = fieldWords(def.key, def.label);
  const w = new Set(words2);
  const has = (...xs) => xs.some((x) => w.has(x));
  if (has(...SKIP_WORDS) || has("time") && has("zone")) return null;
  const parts = [];
  if (has("zip", "zipcode", "postcode", "postalcode", "plz", "pincode") || has("postal", "post", "pin") && has("code")) parts.push("postalCode");
  if (has("country", "countryname", "countrycode", "nation")) parts.push("country");
  if (has("state", "county", "district", "region", "province", "prefecture", "canton", "territory", "oblast", "emirate", "parish")) parts.push("state");
  if (has("city", "town", "locality", "municipality", "village")) parts.push("city");
  let kind = null;
  if (parts.length > 1) kind = "full";
  else if (parts.length === 1) kind = parts[0];
  else if (has("suite", "apt", "apartment", "unit", "floor", "building", "bldg") || has("line", "address", "street", "addr") && has("2", "3")) kind = "line2";
  else if (has("street", "road", "streetaddress") || has("address", "addr", "line") && has("1")) kind = "line1";
  else if (has("address", "addr", "location", "adresse", "direccion", "indirizzo")) kind = "full";
  if (!kind) return null;
  if (def.type === "NUMBER" && kind !== "postalCode") return null;
  const q = words2.find((x) => QUALIFIERS[x] && !(x === "postal" && kind === "postalCode"));
  return {
    kind,
    group: q ? QUALIFIERS[q] : "",
    ...kind === "country" && has("code", "iso", "iso2", "iso3") ? { code: true } : {},
    ...kind === "line1" && has("number", "no", "nr", "num", "house") ? { number: true } : {}
  };
}
function fieldText(v) {
  const s = Array.isArray(v) ? v.filter((x) => typeof x === "string" || typeof x === "number").join(", ") : typeof v === "string" || typeof v === "number" ? String(v) : "";
  const t = s.trim();
  return /^(n\/?a|none|null|undefined|unknown|-+|\.+|0)$/i.test(t) ? "" : t;
}
var joinLine = (a, b) => a ? `${a}, ${b}` : b;
function findRecordAddresses(fields, defs, existing = []) {
  const groups = /* @__PURE__ */ new Map();
  for (const d of defs) {
    const m = classifyAddressField(d);
    if (!m) continue;
    const value = fieldText(fields[d.key]);
    if (!value) continue;
    groups.set(m.group, [...groups.get(m.group) ?? [], { field: d.key, fieldLabel: d.label, value, kind: m.kind, code: m.code, number: m.number }]);
  }
  const out = [];
  for (const [group, sources] of groups) {
    const label = group || null;
    const base = { label, line1: null, line2: null, city: null, state: null, country: null, postalCode: null };
    const used = [];
    const leftover = [];
    const strip = ({ code: _c, number: _n, ...s }) => s;
    const parts = sources.filter((s) => s.kind !== "full").sort((a, b) => Number(!!a.code) - Number(!!b.code));
    const extras = [];
    for (const s of parts) {
      const p = s.kind;
      let v = p === "country" ? matchCountry(s.value, { codes: true }) ?? clean(s.value) : clean(s.value);
      if (p === "line1" && !s.number && /[,;\n]/.test(s.value)) {
        const parsed = parseAddress(s.value);
        if (parsed.line1) {
          v = parsed.line1;
          extras.push({ ...parsed, line1: null });
        }
      }
      const cur = base[p];
      if (s.number) base.line1 = cur ? `${cur} ${v}` : v;
      else if (!cur) base[p] = v;
      else if (foldPart(cur) === foldPart(v)) {
      } else if (p === "line1" || p === "line2") base.line2 = joinLine(base.line2, v);
      else {
        leftover.push(strip(s));
        continue;
      }
      used.push(strip(s));
    }
    const fulls = [];
    for (const s of sources.filter((x) => x.kind === "full")) {
      const same = fulls.find((f) => foldPart(f.value) === foldPart(s.value));
      if (same) same.sources.push(strip(s));
      else fulls.push({ value: s.value, sources: [strip(s)] });
    }
    const known2 = () => ADDRESS_PARTS.map((p) => foldPart(base[p])).filter(Boolean);
    const fillFrom = (parsed) => {
      for (const p of ADDRESS_PARTS) {
        const v = parsed[p];
        if (!v) continue;
        if (!base[p]) base[p] = v;
        else if (foldPart(base[p]) !== foldPart(v) && !known2().some((k) => k.includes(foldPart(v)))) base.line2 = joinLine(base.line2, v);
      }
    };
    extras.forEach(fillFrom);
    if (fulls[0]) {
      fillFrom(parseAddress(fulls[0].value));
      used.push(...fulls[0].sources);
    }
    if (used.length) out.push({ key: group, address: normalizeAddress(base), sources: used, status: "new", leftover });
    fulls.slice(1).forEach((f, i) => out.push({ key: `${group}#${i + 2}`, address: normalizeAddress({ ...parseAddress(f.value), label }), sources: f.sources, status: "new", leftover: [] }));
  }
  const known = [...existing];
  for (const c of out) {
    if (known.some((a) => addressCoveredBy(c.address, a))) c.status = "duplicate";
    else known.push(c.address);
  }
  return out;
}
var ADDRESS_TARGETS = ["contacts", "organizations"];
var addressMergerScanSchema = z5.object({
  target: z5.enum(ADDRESS_TARGETS),
  after: z5.string().nullish(),
  ids: z5.array(z5.string()).max(1e4).optional()
});
var addressMergerApplySchema = z5.object({
  target: z5.enum(ADDRESS_TARGETS),
  items: z5.array(
    z5.object({
      recordId: z5.string(),
      key: z5.string().max(100),
      /** the address as reviewed (it may have been edited) */
      address: addressSchema,
      /** the fields and values it was made from, to check nothing changed since the scan */
      sources: z5.array(z5.object({ field: z5.string().max(200), value: z5.string() })).min(1).max(50)
    })
  ).min(1).max(500),
  /** clear the fields the address was made from (otherwise it is only copied) */
  removeFromSource: z5.boolean().default(true)
});
var addressCandidateKey = (recordId, key) => `${recordId}|${key}`;

// src/contacts.ts
var optionalText = (max = 500) => z6.string().trim().max(max).nullable().optional().transform((v) => v === "" ? null : v);
var customFieldsSchema = z6.record(z6.string(), z6.unknown());
var EMAIL_LABELS = ["Work", "Personal", "Other"];
var PHONE_LABELS = ["Mobile", "WhatsApp", "Work", "Home", "Other"];
var MOBILE_LABEL = "Mobile";
var contactEmailItemSchema = z6.object({
  email: z6.string().trim().toLowerCase().email(),
  label: optionalText(40),
  isPrimary: z6.boolean().optional()
});
var contactPhoneItemSchema = z6.object({
  number: z6.string().trim().min(1).max(60),
  label: optionalText(40),
  isPrimary: z6.boolean().optional()
});
var contactSystemFieldsSchema = z6.object({
  firstName: optionalText(120),
  lastName: optionalText(120),
  email: z6.string().trim().toLowerCase().email().nullable().optional().or(z6.literal("").transform(() => null)),
  phone: optionalText(60),
  mobile: optionalText(60),
  jobTitle: optionalText(160),
  /** The primary organization; without `organizationIds` it is set as the primary and the others are kept. */
  organizationId: z6.string().uuid().nullable().optional(),
  /** Every organization the contact belongs to, primary first; replaces the contact's list. */
  organizationIds: z6.array(z6.string().uuid()).max(50).optional(),
  lifecycleStage: optionalText(60),
  leadStatus: optionalText(60),
  source: optionalText(60),
  /** false = opted out: never emailed, and every address of the contact goes on the suppression list */
  subscribed: z6.boolean().nullable().optional().transform((v) => v ?? void 0),
  /**
   * Every email address in order; replaces the contact's list. The one marked primary (else the first) becomes `email`.
   * Without it, `email` alone sets the primary address and keeps the others.
   */
  emails: z6.array(contactEmailItemSchema).max(20).optional(),
  /**
   * Every phone number in order; replaces the contact's list. The primary one fills `phone`, the first labelled
   * Mobile fills `mobile`. Without it, `phone` / `mobile` alone change just those numbers.
   */
  phones: z6.array(contactPhoneItemSchema).max(20).optional(),
  /** Every postal address in order; replaces the contact's list. */
  addresses: addressListSchema.optional()
});
var createContactSchema = contactSystemFieldsSchema.extend({
  fields: customFieldsSchema.optional(),
  tagIds: z6.array(z6.string().uuid()).optional()
}).refine((c) => !!(c.firstName || c.lastName || c.email || c.emails?.length), {
  message: "A contact needs at least a first name, last name or email",
  path: ["firstName"]
});
var updateContactSchema = contactSystemFieldsSchema.partial().extend({
  fields: customFieldsSchema.optional()
});
var listQuerySchema = paginationSchema.extend({
  filter: z6.string().optional().transform((s, ctx) => {
    if (!s) return void 0;
    try {
      const parsed = ruleGroupSchema.safeParse(JSON.parse(s));
      if (!parsed.success) {
        ctx.addIssue({ code: "custom", message: "Invalid filter rules" });
        return z6.NEVER;
      }
      return parsed.data;
    } catch {
      ctx.addIssue({ code: "custom", message: "filter must be JSON" });
      return z6.NEVER;
    }
  }),
  ids: z6.string().optional().transform((s) => s ? s.split(",").filter(Boolean) : void 0)
});
var bulkIdsSchema = z6.object({ ids: z6.array(z6.string().uuid()).min(1).max(5e3) });

// src/organizations.ts
import { z as z7 } from "zod";
var optionalText2 = (max = 500) => z7.string().trim().max(max).nullable().optional().transform((v) => v === "" ? null : v);
var organizationSystemFieldsSchema = z7.object({
  name: z7.string().trim().min(1).max(200),
  domain: z7.string().trim().toLowerCase().max(253).nullable().optional().transform((v) => v ? v.replace(/^https?:\/\//, "").replace(/^www\./, "").split("/")[0] : null),
  website: optionalText2(500),
  industry: optionalText2(120),
  type: optionalText2(60),
  employees: z7.number().int().min(0).nullable().optional(),
  annualRevenue: z7.number().min(0).nullable().optional(),
  phone: optionalText2(60),
  /** Every postal address in order; replaces the organization's list. */
  addresses: addressListSchema.optional()
});
var createOrganizationSchema = organizationSystemFieldsSchema.extend({
  fields: z7.record(z7.string(), z7.unknown()).optional()
});
var updateOrganizationSchema = organizationSystemFieldsSchema.partial().extend({
  fields: z7.record(z7.string(), z7.unknown()).optional(),
  /** one of the organization's linked contacts */
  mainContactId: z7.string().min(1).optional()
});

// src/activities.ts
import { z as z8 } from "zod";
var createNoteSchema = z8.object({
  type: z8.enum(["NOTE", "CALL", "MEETING"]),
  body: z8.string().trim().min(1).max(2e4),
  contactId: z8.string().uuid().optional(),
  organizationId: z8.string().uuid().optional(),
  dealId: z8.string().uuid().optional(),
  occurredAt: z8.string().datetime().optional()
});
var listActivitiesSchema = paginationSchema.extend({
  contactId: z8.string().uuid().optional(),
  organizationId: z8.string().uuid().optional(),
  dealId: z8.string().uuid().optional(),
  types: z8.string().optional().transform((s) => s ? s.split(",").filter((t) => ACTIVITY_TYPES.includes(t)) : void 0)
});

// src/tasks.ts
import { z as z9 } from "zod";
var TASK_PRIORITIES = ["LOW", "MEDIUM", "HIGH", "URGENT"];
var MAX_RICH_TEXT = 5e4;
var createTaskSchema = z9.object({
  title: z9.string().trim().min(1).max(300),
  description: z9.string().trim().max(MAX_RICH_TEXT).nullable().optional(),
  dueAt: z9.string().datetime().nullable().optional(),
  assigneeUserId: z9.string().uuid().nullable().optional(),
  contactId: z9.string().uuid().nullable().optional(),
  dealId: z9.string().uuid().nullable().optional(),
  organizationId: z9.string().uuid().nullable().optional(),
  priority: z9.enum(TASK_PRIORITIES).nullable().optional(),
  projectId: z9.string().uuid().nullable().optional(),
  sectionId: z9.string().uuid().nullable().optional(),
  /** makes it a subtask of this task */
  parentId: z9.string().uuid().nullable().optional(),
  /** add at the top of its section instead of the end */
  atStart: z9.boolean().optional()
});
var updateTaskSchema = createTaskSchema.omit({ atStart: true }).partial().extend({
  completed: z9.boolean().optional()
});
var listTasksSchema = paginationSchema.extend({
  status: z9.enum(["open", "done", "all"]).default("open"),
  assignee: z9.enum(["me", "all"]).default("all"),
  contactId: z9.string().uuid().optional(),
  organizationId: z9.string().uuid().optional(),
  dealId: z9.string().uuid().optional(),
  due: z9.enum(["overdue", "today", "week", "all"]).default("all")
});
var queryTasksSchema = z9.object({
  scope: z9.enum(["project", "mine"]).default("project"),
  status: z9.enum(["open", "done", "all"]).default("open"),
  /** completed tasks finished more than this many days ago are left out of status=all (0 = keep all) */
  doneWithinDays: z9.coerce.number().int().min(0).max(3650).default(0)
});
var ids = z9.array(z9.string().uuid()).min(1).max(1e3);
var bulkUpdateTasksSchema = z9.object({
  ids,
  patch: updateTaskSchema.pick({ assigneeUserId: true, dueAt: true, priority: true, sectionId: true, projectId: true, completed: true })
});
var bulkDeleteTasksSchema = z9.object({ ids });
var reorderTasksSchema = z9.object({
  sectionId: z9.string().uuid().nullable().optional(),
  parentId: z9.string().uuid().nullable().optional(),
  ids: z9.array(z9.string().uuid()).max(2e3)
});
var createTaskSectionSchema = z9.object({
  name: z9.string().trim().min(1).max(120),
  projectId: z9.string().uuid().nullable().optional()
});
var updateTaskSectionSchema = z9.object({ name: z9.string().trim().min(1).max(120) });
var reorderTaskSectionsSchema = z9.object({ ids: z9.array(z9.string().uuid()).max(500) });
var taskCommentSchema = z9.object({ body: z9.string().trim().min(1).max(MAX_RICH_TEXT) });
var taskActivityQuerySchema = z9.object({
  /** only activity on tasks assigned to the signed-in user */
  mine: z9.enum(["true", "false"]).default("false").transform((v) => v === "true"),
  /** ISO time: activity older than this (paging) */
  before: z9.string().datetime().optional(),
  limit: z9.coerce.number().int().min(1).max(100).default(50)
});

// src/projects.ts
import { z as z10 } from "zod";
var PROJECTS_MIN_ACTIVE = 2;
var PROJECT_HEADER = "x-project-id";
var optionalText3 = (max) => z10.string().trim().max(max).nullable().optional().transform((v) => v ? v : null);
var hexColor = z10.string().trim().nullable().optional().transform((v) => {
  if (!v) return null;
  const h = v.replace(/^#/, "").toLowerCase();
  return `#${h.length === 3 ? h.replace(/./g, (c) => c + c) : h}`;
}).refine((v) => v === null || /^#[0-9a-f]{6}$/.test(v), "Use a hex colour like #2f5bea");
var PROJECT_COLOR_KEYS = ["primaryColor", "secondaryColor", "accentColor", "gradientFrom", "gradientTo"];
var createProjectSchema = z10.object({
  name: z10.string().trim().min(1).max(120),
  /** short form of the name, e.g. "SCon" for StartupCon */
  acronym: optionalText3(20),
  description: optionalText3(500),
  url: z10.string().trim().max(500).nullable().optional().transform((v) => v ? /^https?:\/\//i.test(v) ? v : `https://${v}` : null).refine((v) => v === null || /^https?:\/\/[^\s/]+\.[^\s]+$|^https?:\/\/localhost/i.test(v), "Enter a website address like https://example.com"),
  primaryColor: hexColor,
  secondaryColor: hexColor,
  accentColor: hexColor,
  /** gradient start and end; set both or neither */
  gradientFrom: hexColor,
  gradientTo: hexColor,
  /** contacts who own the project, in display order */
  ownerIds: z10.array(z10.string().uuid()).max(20).optional()
});
var updateProjectSchema = createProjectSchema.partial();
var projectLabelSchema = z10.string().uuid().nullable().optional();

// src/audience.ts
import { z as z11 } from "zod";
var createTagSchema = z11.object({
  name: z11.string().trim().min(1).max(60),
  color: z11.string().trim().regex(/^#[0-9a-fA-F]{6}$/).nullable().optional(),
  /** Nests the tag under another tag; null puts it at the top level. */
  parentId: z11.string().min(1).nullable().optional(),
  projectId: projectLabelSchema
});
var updateTagSchema = createTagSchema.partial();
var createListSchema = z11.object({
  name: z11.string().trim().min(1).max(120),
  description: z11.string().trim().max(1e3).nullable().optional(),
  projectId: projectLabelSchema
});
var updateListSchema = createListSchema.partial();
var createSegmentSchema = z11.object({
  name: z11.string().trim().min(1).max(120),
  description: z11.string().trim().max(1e3).nullable().optional(),
  rules: ruleGroupSchema,
  projectId: projectLabelSchema
});
var updateSegmentSchema = createSegmentSchema.partial();
var previewSegmentSchema = z11.object({ rules: ruleGroupSchema });
var mergeSegmentsSchema = z11.object({
  segmentIds: z11.array(z11.string().uuid()).min(2).max(50),
  name: z11.string().trim().min(1).max(120),
  description: z11.string().trim().max(1e3).nullable().optional()
});
var bulkSelectionSchema = z11.object({
  ids: z11.array(z11.string().uuid()).max(1e4).optional(),
  all: z11.object({
    q: z11.string().trim().optional(),
    filter: ruleGroupSchema.optional(),
    excludeIds: z11.array(z11.string().uuid()).optional()
  }).optional()
}).refine((s) => s.ids && s.ids.length > 0 || s.all, { message: "Select at least one contact" });
var bulkTagsSchema = z11.object({
  selection: bulkSelectionSchema,
  add: z11.array(z11.string().uuid()).optional(),
  remove: z11.array(z11.string().uuid()).optional()
});
var bulkListsSchema = z11.object({
  selection: bulkSelectionSchema,
  add: z11.array(z11.string().uuid()).optional(),
  remove: z11.array(z11.string().uuid()).optional()
});
var bulkSegmentsSchema = z11.object({
  selection: bulkSelectionSchema,
  add: z11.array(z11.string().uuid()).optional(),
  remove: z11.array(z11.string().uuid()).optional()
});
var bulkUpdateSchema = z11.object({
  selection: bulkSelectionSchema,
  data: z11.object({
    firstName: z11.string().trim().max(120).nullable().optional(),
    lastName: z11.string().trim().max(120).nullable().optional(),
    jobTitle: z11.string().trim().max(160).nullable().optional(),
    lifecycleStage: z11.string().trim().max(60).nullable().optional(),
    leadStatus: z11.string().trim().max(60).nullable().optional(),
    source: z11.string().trim().max(60).nullable().optional(),
    subscribed: z11.boolean().optional(),
    organizationId: z11.string().uuid().nullable().optional(),
    fields: z11.record(z11.string(), z11.unknown()).optional()
  })
});
var bulkDeleteSchema = z11.object({ selection: bulkSelectionSchema });
var setContactTagsSchema = z11.object({ tagIds: z11.array(z11.string().uuid()) });
var listMembersSchema = z11.object({ selection: bulkSelectionSchema });

// src/email.ts
import { z as z12 } from "zod";
var domainName = z12.string().trim().toLowerCase().regex(/^(?!-)[a-z0-9-]{1,63}(?<!-)(\.[a-z0-9-]{1,63})+$/, "Enter a bare domain like mail.example.com");
var createDomainSchema = z12.object({
  domain: domainName,
  inboundEnabled: z12.boolean().optional()
});
var updateDomainSchema = z12.object({ inboundEnabled: z12.boolean().optional(), verified: z12.boolean().optional() });
var localPart = z12.string().trim().toLowerCase().regex(/^[a-z0-9._%+-]{1,64}$/, "Letters, numbers, dots, underscores, hyphens");
var createAgentSchema = z12.object({
  name: z12.string().trim().min(1).max(120),
  position: z12.string().trim().max(120).nullable().optional(),
  localPart,
  domainId: z12.string().uuid(),
  signatureHtml: z12.string().max(2e4).nullable().optional(),
  avatarUrl: z12.string().trim().url().nullable().optional().or(z12.literal("").transform(() => null)),
  isActive: z12.boolean().optional()
});
var updateAgentSchema = createAgentSchema.partial();
var sendEmailSchema = z12.object({
  agentId: z12.string().uuid(),
  contactId: z12.string().uuid(),
  subject: z12.string().trim().min(1).max(500),
  html: z12.string().min(1).max(2e5),
  /** reply inside an existing thread */
  threadId: z12.string().uuid().optional(),
  /** override recipient (defaults to the contact's primary email) */
  to: z12.string().trim().toLowerCase().email().optional()
});
var replyThreadSchema = z12.object({
  agentId: z12.string().uuid().optional(),
  html: z12.string().min(1).max(2e5)
});
var updateThreadSchema = z12.object({
  unread: z12.boolean().optional(),
  archived: z12.boolean().optional(),
  contactId: z12.string().uuid().nullable().optional()
});
var listThreadsSchema = paginationSchema.extend({
  domainId: z12.string().uuid().optional(),
  contactId: z12.string().uuid().optional(),
  status: z12.enum(["inbox", "unread", "archived", "all"]).default("inbox")
});

// src/merge-tags.ts
var MERGE_TAGS = [
  { tag: "first_name", label: "First name" },
  { tag: "last_name", label: "Last name" },
  { tag: "full_name", label: "Full name" },
  { tag: "email", label: "Email" },
  { tag: "job_title", label: "Job title" },
  { tag: "phone", label: "Phone" },
  { tag: "organization_name", label: "Organization" },
  { tag: "organization_domain", label: "Organization domain" },
  { tag: "organization_website", label: "Organization website" },
  { tag: "organization_industry", label: "Organization industry" },
  { tag: "organization_phone", label: "Organization phone" },
  { tag: "organization_employees", label: "Organization employees" },
  { tag: "agent_name", label: "Agent name" },
  { tag: "agent_position", label: "Agent position" },
  { tag: "agent_email", label: "Agent email" },
  { tag: "unsubscribe_url", label: "Unsubscribe link" }
];
var ORG_FIELD_PREFIX = "org_";
function mergeValues(ctx) {
  const c = ctx.contact ?? {};
  const a = ctx.agent ?? {};
  const out = {
    first_name: c.firstName ?? "",
    last_name: c.lastName ?? "",
    full_name: [c.firstName, c.lastName].filter(Boolean).join(" "),
    email: c.email ?? "",
    job_title: c.jobTitle ?? "",
    phone: c.phone ?? "",
    organization_name: c.organization?.name ?? "",
    organization_domain: c.organization?.domain ?? "",
    organization_website: c.organization?.website ?? "",
    organization_industry: c.organization?.industry ?? "",
    organization_phone: c.organization?.phone ?? "",
    organization_employees: c.organization?.employees != null ? String(c.organization.employees) : "",
    agent_name: a.name ?? "",
    agent_position: a.position ?? "",
    agent_email: a.email ?? ""
  };
  for (const [k, v] of Object.entries(c.fields ?? {})) {
    if (v === null || v === void 0) continue;
    out[k] = Array.isArray(v) ? v.join(", ") : String(v);
  }
  for (const [k, v] of Object.entries(c.organization?.fields ?? {})) {
    if (v === null || v === void 0) continue;
    out[`${ORG_FIELD_PREFIX}${k}`] = Array.isArray(v) ? v.join(", ") : String(v);
  }
  for (const [k, v] of Object.entries(ctx.extra ?? {})) out[k] = v ?? "";
  return out;
}
var TAG_RE = /\{\{\s*([a-zA-Z0-9_]+)\s*(?:\|\s*([^}]*?)\s*)?\}\}/g;
function renderMergeTags(template, ctx, escapeHtml = true) {
  const values = mergeValues(ctx);
  return template.replace(TAG_RE, (_m, key, fallback) => {
    const v = values[key];
    const picked = v !== void 0 && v !== "" ? v : fallback ?? "";
    return escapeHtml ? escape(picked) : picked;
  });
}
function findMergeTags(template) {
  const found = /* @__PURE__ */ new Set();
  for (const m of template.matchAll(TAG_RE)) found.add(m[1]);
  return [...found];
}
function escape(s) {
  return s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
}

// src/deals.ts
import { z as z13 } from "zod";
var createPipelineSchema = z13.object({
  name: z13.string().trim().min(1).max(120),
  isDefault: z13.boolean().optional(),
  /** optional initial stages; otherwise a sensible default set is created */
  stages: z13.array(z13.object({ name: z13.string().trim().min(1).max(80), kind: z13.enum(["OPEN", "WON", "LOST"]).default("OPEN"), probability: z13.number().int().min(0).max(100).nullable().optional(), color: z13.string().trim().max(20).nullable().optional() })).optional()
});
var updatePipelineSchema = z13.object({ name: z13.string().trim().min(1).max(120).optional(), isDefault: z13.boolean().optional() });
var createStageSchema = z13.object({
  name: z13.string().trim().min(1).max(80),
  kind: z13.enum(["OPEN", "WON", "LOST"]).default("OPEN"),
  probability: z13.number().int().min(0).max(100).nullable().optional(),
  color: z13.string().trim().max(20).nullable().optional()
});
var updateStageSchema = createStageSchema.partial();
var reorderStagesSchema = z13.object({ ids: z13.array(z13.string().uuid()).min(1) });
var deleteStageSchema = z13.object({ moveDealsTo: z13.string().uuid().optional() });
var optionalText4 = (max = 500) => z13.string().trim().max(max).nullable().optional().transform((v) => v === "" ? null : v);
var dealSystemFieldsSchema = z13.object({
  title: z13.string().trim().min(1).max(300),
  pipelineId: z13.string().uuid().optional(),
  stageId: z13.string().uuid().optional(),
  value: z13.number().min(0).nullable().optional(),
  // blank (null) keeps the workspace default on create and the current currency on update
  currency: z13.string().trim().length(3).toUpperCase().nullable().optional().transform((v) => v ?? void 0),
  contactId: z13.string().uuid().nullable().optional(),
  organizationId: z13.string().uuid().nullable().optional(),
  agentId: z13.string().uuid().nullable().optional(),
  expectedCloseAt: z13.string().nullable().optional(),
  lostReason: optionalText4(500),
  source: optionalText4(60),
  description: optionalText4(5e3)
});
var createDealSchema = dealSystemFieldsSchema.extend({ fields: z13.record(z13.string(), z13.unknown()).optional() });
var updateDealSchema = dealSystemFieldsSchema.partial().extend({ fields: z13.record(z13.string(), z13.unknown()).optional() });
var moveDealSchema = z13.object({
  stageId: z13.string().uuid(),
  /** position within the target stage (0 = top); appended when omitted */
  sortIndex: z13.number().int().min(0).optional(),
  lostReason: z13.string().trim().max(500).nullable().optional()
});
var listDealsSchema = paginationSchema.extend({
  pipelineId: z13.string().uuid().optional(),
  stageId: z13.string().uuid().optional(),
  contactId: z13.string().uuid().optional(),
  organizationId: z13.string().uuid().optional(),
  agentId: z13.string().uuid().optional(),
  status: z13.enum(["open", "won", "lost", "all"]).default("open")
});

// src/forms.ts
import { z as z14 } from "zod";
var formFieldSchema = z14.object({
  /** contact field key (system or custom) */
  key: z14.string().min(1),
  label: z14.string().trim().max(120).optional(),
  placeholder: z14.string().trim().max(200).optional(),
  helpText: z14.string().trim().max(300).optional(),
  required: z14.boolean().optional(),
  /** full or half width on the hosted page */
  width: z14.enum(["full", "half"]).optional()
});
var formSettingsSchema = z14.object({
  submitLabel: z14.string().trim().max(60).optional(),
  consentText: z14.string().trim().max(500).optional(),
  /** show a consent box that must be ticked to submit (ticking it re-subscribes an unsubscribed contact) */
  requireConsent: z14.boolean().optional(),
  dealTitle: z14.string().trim().max(200).optional(),
  dealValue: z14.number().min(0).nullable().optional(),
  agentId: z14.string().uuid().nullable().optional(),
  /** keep existing values on an existing contact (only fill blanks) vs overwrite with submitted values */
  overwriteExisting: z14.boolean().optional(),
  theme: z14.object({ primaryColor: z14.string().optional(), background: z14.string().optional() }).optional(),
  /** an email sent as an agent to the person who submitted, right after each submission */
  followUp: z14.object({
    enabled: z14.boolean(),
    agentId: z14.string().uuid().nullable().optional(),
    subject: z14.string().trim().max(500).optional(),
    html: z14.string().max(1e5).optional()
  }).optional()
});
var slugSchema = z14.string().trim().toLowerCase().regex(/^[a-z0-9]+(?:-[a-z0-9]+)*$/, "Lowercase letters, numbers and hyphens").min(2).max(80);
var createFormSchema = z14.object({
  name: z14.string().trim().min(1).max(120),
  slug: slugSchema.optional(),
  description: z14.string().trim().max(1e3).nullable().optional(),
  fields: z14.array(formFieldSchema).min(1),
  tagIds: z14.array(z14.string().uuid()).optional(),
  /** static lists every submitter is added to */
  listIds: z14.array(z14.string().uuid()).optional(),
  /** segments every submitter is added to by hand (kept whatever the segment rules say) */
  segmentIds: z14.array(z14.string().uuid()).optional(),
  /** the hosted page shows this project's logo and colours */
  projectId: z14.string().uuid().nullable().optional(),
  pipelineId: z14.string().uuid().nullable().optional(),
  stageId: z14.string().uuid().nullable().optional(),
  successMessage: z14.string().trim().max(1e3).nullable().optional(),
  redirectUrl: z14.string().trim().url().nullable().optional().or(z14.literal("").transform(() => null)),
  isActive: z14.boolean().optional(),
  settings: formSettingsSchema.optional()
});
var updateFormSchema = createFormSchema.partial();
var listSubmissionsSchema = paginationSchema;
var isHex = (c) => !!c && /^#[0-9a-fA-F]{6}$/.test(c);
function formTheme(theme, project) {
  const out = { ...theme };
  if (!project) return out;
  if (!out.primaryColor) {
    const main = [project.primaryColor, project.accentColor].find(isHex);
    if (main) out.primaryColor = main;
  }
  if (!out.background && isHex(project.gradientFrom) && isHex(project.gradientTo)) out.background = `linear-gradient(135deg, ${project.gradientFrom} 0%, ${project.gradientTo} 100%)`;
  return out;
}
var formProject = (project) => project ? { name: project.name, logoFileId: project.logoFileId ?? project.iconFileId ?? null } : null;

// src/campaigns.ts
import { z as z16 } from "zod";

// src/email-design.ts
import { z as z15 } from "zod";
var SIGNATURE_SLOT = "<!--crm-signature-->";
var NO_SIGNATURE = "<!--crm-no-signature-->";
var EMAIL_FONTS = {
  sans: { label: "Clean (sans-serif)", stack: "Helvetica, Arial, sans-serif" },
  modern: { label: "Modern", stack: "'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif" },
  serif: { label: "Classic (serif)", stack: "Georgia, 'Times New Roman', serif" },
  mono: { label: "Typewriter", stack: "'Courier New', Courier, monospace" }
};
var color = z15.string().regex(/^#[0-9a-fA-F]{6}$/, "Use a colour like #50a046");
var align = z15.enum(["left", "center", "right"]);
var textColor = color.optional();
var id = z15.string().min(1).max(40);
var blockPaddingSchema = z15.object({ top: z15.number().int().min(0).max(120), bottom: z15.number().int().min(0).max(120), sides: z15.number().int().min(0).max(80) });
var box = { padding: blockPaddingSchema.optional(), background: color.optional() };
var emailBlockSchema = z15.discriminatedUnion("type", [
  z15.object({ id, type: z15.literal("heading"), text: z15.string().max(500), level: z15.union([z15.literal(1), z15.literal(2), z15.literal(3)]), align, color: textColor, ...box }),
  z15.object({ id, type: z15.literal("text"), html: z15.string().max(1e5), align, color: textColor, ...box }),
  z15.object({ id, type: z15.literal("button"), label: z15.string().max(120), url: z15.string().max(2e3), align, fullWidth: z15.boolean().optional(), buttonColor: color.optional(), labelColor: color.optional(), ...box }),
  z15.object({ id, type: z15.literal("image"), src: z15.string().max(2e3), alt: z15.string().max(300), href: z15.string().max(2e3).optional(), width: z15.number().int().min(10).max(100), align, fullBleed: z15.boolean().optional(), ...box }),
  z15.object({ id, type: z15.literal("divider"), ...box }),
  z15.object({ id, type: z15.literal("spacer"), height: z15.number().int().min(4).max(160), ...box })
]);
var emailStyleSchema = z15.object({
  accentColor: color,
  /** link text in the email; the accent colour when unset */
  linkColor: color.optional(),
  buttonTextColor: color,
  textColor: color,
  pageBackground: color,
  emailBackground: color,
  font: z15.enum(Object.keys(EMAIL_FONTS)),
  logoUrl: z15.string().max(2e3),
  logoWidth: z15.number().int().min(40).max(560),
  footerText: z15.string().max(2e3),
  showSignature: z15.boolean()
});
var emailDesignSchema = z15.object({
  version: z15.literal(1),
  preheader: z15.string().max(300),
  style: emailStyleSchema,
  blocks: z15.array(emailBlockSchema).max(200)
});
var DEFAULT_EMAIL_STYLE = {
  accentColor: "#50a046",
  buttonTextColor: "#ffffff",
  textColor: "#1f2937",
  pageBackground: "#f4f4f5",
  emailBackground: "#ffffff",
  font: "sans",
  logoUrl: "",
  logoWidth: 180,
  footerText: "",
  showSignature: true
};
var newBlockId = () => Math.random().toString(36).slice(2, 10);
function newEmailBlock(type) {
  const blockId = newBlockId();
  switch (type) {
    case "heading":
      return { id: blockId, type, text: "Your heading", level: 1, align: "left" };
    case "text":
      return { id: blockId, type, html: "<p>Write your message here.</p>", align: "left" };
    case "button":
      return { id: blockId, type, label: "Find out more", url: "https://", align: "center" };
    case "image":
      return { id: blockId, type, src: "", alt: "", width: 100, align: "center" };
    case "divider":
      return { id: blockId, type };
    case "spacer":
      return { id: blockId, type, height: 24 };
  }
}
function readableOn(hex) {
  const lum = (h) => {
    const [r, g, b] = [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16) / 255).map((c) => c <= 0.03928 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
    return 0.2126 * r + 0.7152 * g + 0.0722 * b;
  };
  const bg = lum(hex);
  return (1 + 0.05) / (bg + 0.05) >= 3 ? "#ffffff" : "#111827";
}
var HEX = /^#[0-9a-fA-F]{6}$/;
function projectEmailStyle(p) {
  if (!p) return {};
  const out = {};
  if (p.logoFileId) out.logoUrl = `/api/files/${p.logoFileId}`;
  const brand = [p.primaryColor, p.accentColor].find((c) => !!c && HEX.test(c));
  if (brand) {
    out.accentColor = brand.toLowerCase();
    out.buttonTextColor = readableOn(brand);
  }
  return out;
}
function defaultEmailDesign(style = {}) {
  return {
    version: 1,
    preheader: "",
    style: { ...DEFAULT_EMAIL_STYLE, ...style },
    blocks: [
      { id: newBlockId(), type: "heading", text: "Hi {{first_name|there}},", level: 1, align: "left" },
      { id: newBlockId(), type: "text", html: "<p>Write your message here.</p>", align: "left" },
      { id: newBlockId(), type: "button", label: "Find out more", url: "https://", align: "center" }
    ]
  };
}
function designFromHtml(html, style = {}) {
  return { version: 1, preheader: "", style: { ...DEFAULT_EMAIL_STYLE, ...style }, blocks: html.trim() ? [{ id: newBlockId(), type: "text", html, align: "left" }] : [] };
}
var BLOCK_LABELS = { heading: "Heading", text: "Text", button: "Button", image: "Image", divider: "Divider", spacer: "Spacer" };
var MERGE_ONLY = /^\{\{\s*[a-zA-Z0-9_]+\s*(\|[^}]*)?\}\}$/;
var FULL_URL = /^https?:\/\/[^\s/]+\.[^\s]+$|^https?:\/\/localhost(:\d+)?(\/\S*)?$/i;
var isUsableLink = (url) => FULL_URL.test(url.trim()) || /^mailto:\S+@\S+$/i.test(url.trim()) || MERGE_ONLY.test(url.trim());
function emailDesignProblems(d) {
  const out = [];
  if (!d.blocks.length) out.push("The email has no content. Add a block.");
  d.blocks.forEach((b, i) => {
    const where = `Block ${i + 1} (${BLOCK_LABELS[b.type]})`;
    if (b.type === "heading" && !b.text.trim()) out.push(`${where} is empty.`);
    if (b.type === "text" && !stripTags(b.html).trim()) out.push(`${where} is empty.`);
    if (b.type === "button") {
      if (!b.label.trim()) out.push(`${where} has no label.`);
      if (!isUsableLink(b.url)) out.push(`${where} needs a full link starting with https:// (it is "${b.url}").`);
    }
    if (b.type === "image") {
      if (!b.src.trim()) out.push(`${where} has no picture. Upload one or paste its address.`);
      if (b.href?.trim() && !isUsableLink(b.href)) out.push(`${where} links to "${b.href}", which is not a full https:// link.`);
    }
  });
  return out;
}
var stripTags = (html) => html.replace(/<[^>]*>/g, "").replace(/&nbsp;/g, " ");
var esc = (s) => s.replace(/[&<>"']/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" })[ch]);
var EMAIL_BANNER = { width: 1200, height: 500 };
var asset = (src, base) => src.startsWith("/") ? `${base}${src}` : src;
var HEADING_SIZES = { 1: 28, 2: 22, 3: 18 };
function defaultBlockPadding(b) {
  switch (b.type) {
    case "heading":
      return { top: 16, bottom: 4, sides: 40 };
    case "button":
    case "divider":
      return { top: 16, bottom: 16, sides: 40 };
    case "spacer":
      return { top: 0, bottom: 0, sides: 40 };
    case "image":
      return { top: 8, bottom: 8, sides: b.fullBleed ? 0 : 40 };
    default:
      return { top: 8, bottom: 8, sides: 40 };
  }
}
function renderEmailDesign(d, opts = {}) {
  const s = d.style;
  const base = (opts.assetBase ?? "").replace(/\/$/, "");
  const font = EMAIL_FONTS[s.font]?.stack ?? EMAIL_FONTS.sans.stack;
  const linkColor = s.linkColor ?? s.accentColor;
  const link = `color:${linkColor};text-decoration:underline`;
  const row = (inner, b, fallback) => {
    const attrs = opts.editor ? ` data-block="${b.id}"${opts.selectedId === b.id ? ' data-selected="1"' : ""}` : "";
    const p = b.padding ?? fallback ?? defaultBlockPadding(b);
    const bg = b.background ? ` bgcolor="${b.background}"` : "";
    return `<tr><td${p.sides > 20 ? ' class="crm-pad"' : ""}${attrs}${bg} style="padding:${p.top}px ${p.sides}px ${p.bottom}px${b.background ? `;background:${b.background}` : ""}">${inner}</td></tr>`;
  };
  const first = d.blocks[0];
  const bleedTop = !s.logoUrl.trim() && first?.type === "image" && !!first.fullBleed && !!first.src.trim() && !first.padding;
  const blocks = d.blocks.map((b) => {
    switch (b.type) {
      case "heading": {
        const size = HEADING_SIZES[b.level];
        return row(`<h${b.level} style="margin:0;font-family:${font};font-size:${size}px;line-height:1.25;font-weight:700;color:${b.color ?? s.textColor};text-align:${b.align}">${esc(b.text)}</h${b.level}>`, b);
      }
      case "text": {
        const html = b.html.replace(/<p>/g, '<p style="margin:0 0 12px">').replace(/<(ul|ol)>/g, '<$1 style="margin:0 0 12px;padding-left:22px">').replace(/<a\b(?![^>]*\bstyle=)/g, `<a style="${link}"`);
        return row(`<div style="font-family:${font};font-size:16px;line-height:1.6;color:${b.color ?? s.textColor};text-align:${b.align}">${html}</div>`, b);
      }
      case "button": {
        const width = b.fullWidth ? ' width="100%"' : "";
        const fill = b.buttonColor ?? s.accentColor;
        const label = b.labelColor ?? (b.buttonColor ? readableOn(b.buttonColor) : s.buttonTextColor);
        const btn = `<table role="presentation" cellpadding="0" cellspacing="0" border="0"${width} align="${b.align}"><tr><td align="center" bgcolor="${fill}" style="border-radius:8px;background:${fill}"><a href="${esc(b.url.trim())}" target="_blank" style="display:block;padding:14px 32px;font-family:${font};font-size:16px;font-weight:600;line-height:1.2;color:${label};text-decoration:none;border-radius:8px">${esc(b.label)}</a></td></tr></table>`;
        return row(btn, b);
      }
      case "image": {
        if (!b.src.trim()) return row(`<div style="padding:28px;border:2px dashed #d4d4d8;border-radius:8px;text-align:center;font-family:${font};font-size:13px;color:#a1a1aa">Image: upload a picture or paste its address</div>`, b, { top: 8, bottom: 8, sides: 40 });
        const wrap = (img2) => b.href?.trim() ? `<a href="${esc(b.href.trim())}" target="_blank">${img2}</a>` : img2;
        if (b.fullBleed) {
          const radius = b === first && bleedTop ? "12px 12px 0 0" : "0";
          const img2 = `<img src="${esc(asset(b.src.trim(), base))}" alt="${esc(b.alt)}" width="600" style="display:block;width:100%;max-width:600px;height:auto;border:0;border-radius:${radius}">`;
          return row(`<div style="line-height:0">${wrap(img2)}</div>`, b, b === first && bleedTop ? { top: 0, bottom: 0, sides: 0 } : void 0);
        }
        const img = `<img src="${esc(asset(b.src.trim(), base))}" alt="${esc(b.alt)}" width="${Math.round(520 * b.width / 100)}" style="display:inline-block;width:${b.width}%;max-width:100%;height:auto;border:0;border-radius:6px">`;
        return row(`<div style="text-align:${b.align};line-height:0">${wrap(img)}</div>`, b);
      }
      case "divider":
        return row(`<div style="border-top:1px solid #e4e4e7;font-size:0;line-height:0">&nbsp;</div>`, b);
      case "spacer":
        return row(`<div style="height:${b.height}px;font-size:0;line-height:0">&nbsp;</div>`, b);
    }
  });
  const logo = s.logoUrl.trim() ? `<tr><td align="center" style="padding:32px 40px 8px"><img src="${esc(asset(s.logoUrl.trim(), base))}" alt="" width="${s.logoWidth}" style="display:block;width:${s.logoWidth}px;max-width:100%;height:auto;border:0"></td></tr>` : bleedTop ? "" : `<tr><td style="padding:16px 0 0;font-size:0;line-height:0">&nbsp;</td></tr>`;
  const signature = s.showSignature ? `<tr><td class="crm-pad" style="padding:0 40px;font-family:${font};font-size:15px;line-height:1.5;color:${s.textColor}">${SIGNATURE_SLOT}</td></tr>` : NO_SIGNATURE;
  const footerText = s.footerText.trim() ? `${esc(s.footerText.trim()).replace(/\n/g, "<br>")}<br><br>` : "";
  const preheader = d.preheader.trim() ? `<div style="display:none;max-height:0;overflow:hidden;opacity:0;mso-hide:all">${esc(d.preheader.trim())}${"&#847;&zwnj;&nbsp;".repeat(30)}</div>` : "";
  const editorCss = opts.editor ? `[data-block]{cursor:pointer;outline:2px solid transparent;outline-offset:-2px;transition:outline-color .12s}[data-block]:hover{outline-color:${s.accentColor}55}[data-block][data-selected]{outline-color:${s.accentColor}}` : "";
  return `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1"><meta name="x-apple-disable-message-reformatting"><title></title>
<style>body{margin:0;padding:0}img{-ms-interpolation-mode:bicubic}a{color:${linkColor}}@media (max-width:620px){.crm-card{width:100%!important}.crm-pad{padding-left:20px!important;padding-right:20px!important}}${editorCss}</style></head>
<body style="margin:0;padding:0;background:${s.pageBackground}">${preheader}
<table role="presentation" width="100%" cellpadding="0" cellspacing="0" border="0" bgcolor="${s.pageBackground}" style="background:${s.pageBackground}"><tr><td align="center" style="padding:24px 12px">
<table role="presentation" class="crm-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px;background:${s.emailBackground};border-radius:12px">
${logo}
${blocks.join("\n")}
${signature}
<tr><td style="padding:0 0 24px;font-size:0;line-height:0">&nbsp;</td></tr>
</table>
<table role="presentation" class="crm-card" width="600" cellpadding="0" cellspacing="0" border="0" style="width:600px;max-width:600px"><tr><td class="crm-pad" align="center" style="padding:20px 40px;font-family:${font};font-size:12px;line-height:1.5;color:#71717a">${footerText}Don't want these emails? <a href="{{unsubscribe_url}}" style="color:#71717a;text-decoration:underline">Unsubscribe</a>.</td></tr></table>
</td></tr></table>
</body></html>`;
}

// src/campaigns.ts
var templateFields = z16.object({
  name: z16.string().trim().min(1).max(120),
  subject: z16.string().trim().min(1).max(500),
  /** ignored when `design` is given: the API renders the HTML from the design */
  html: z16.string().max(5e5).optional(),
  design: emailDesignSchema.nullable().optional(),
  agentId: z16.string().uuid().nullable().optional()
});
var createTemplateSchema = templateFields.refine((t) => !!t.design || !!t.html?.trim(), { message: "Add content to the template", path: ["html"] });
var updateTemplateSchema = templateFields.partial();
var idList = z16.array(z16.string().uuid()).optional();
var audienceSchema = z16.object({
  /** when true, every contact is included (before exclusions and eligibility) */
  allContacts: z16.boolean().optional(),
  include: z16.object({
    segmentIds: idList,
    listIds: idList,
    tagIds: idList,
    filter: ruleGroupSchema.optional(),
    /** people picked by hand; exclusions still apply to them */
    contactIds: z16.array(z16.string().uuid()).max(1e4).optional()
  }).optional(),
  exclude: z16.object({ segmentIds: idList, listIds: idList, tagIds: idList }).optional()
});
var CAMPAIGN_STATUSES = ["DRAFT", "SCHEDULED", "VERIFYING", "SENDING", "SENT", "CANCELLED", "FAILED"];
var createCampaignSchema = z16.object({
  name: z16.string().trim().min(1).max(160),
  agentId: z16.string().uuid().nullable().optional(),
  templateId: z16.string().uuid().nullable().optional(),
  subject: z16.string().trim().max(500).optional(),
  /** ignored when `design` is given: the API renders the HTML from the design */
  html: z16.string().max(5e5).optional(),
  design: emailDesignSchema.nullable().optional(),
  audience: audienceSchema.optional(),
  requireVerified: z16.boolean().optional(),
  /** with requireVerified: also send to Unknown and Catch-all, and let delivery or a bounce settle their status */
  includeUnconfirmed: z16.boolean().optional(),
  /** where replies go; empty = the sending agent's address */
  replyTo: z16.union([z16.literal(""), z16.string().trim().toLowerCase().email().max(320)]).nullable().optional().transform((v) => v === "" ? null : v),
  /** project label; picking one puts the project's logo at the top of a designed email */
  projectId: projectLabelSchema
});
var updateCampaignSchema = createCampaignSchema.partial();
var sendCampaignSchema = z16.object({
  /** ISO datetime; omitted = send now */
  scheduledAt: z16.string().datetime().nullable().optional()
});
var testSendSchema = z16.object({
  to: z16.union([z16.string().trim().toLowerCase().email(), z16.array(z16.string().trim().toLowerCase().email()).min(1).max(5)])
});
var previewAudienceSchema = z16.object({ audience: audienceSchema, requireVerified: z16.boolean().optional(), includeUnconfirmed: z16.boolean().optional() });
var audiencePeopleSchema = previewAudienceSchema.extend({
  kind: z16.enum(["eligible", "skipped"]).default("eligible"),
  q: z16.string().trim().max(200).optional(),
  page: z16.number().int().min(1).default(1),
  pageSize: z16.number().int().min(1).max(100).default(50)
});
var resolveEmailsSchema = z16.object({ emails: z16.array(z16.string().trim().toLowerCase()).min(1).max(2e3) });
var listRecipientsSchema = paginationSchema.extend({
  status: z16.enum(["all", "PENDING", "SENT", "DELIVERED", "OPENED", "CLICKED", "BOUNCED", "COMPLAINED", "FAILED", "SKIPPED"]).default("all")
});
var SKIP_REASONS = ["UNSUBSCRIBED", "SUPPRESSED", "NO_EMAIL", "UNVERIFIED", "INVALID", "DUPLICATE_EMAIL"];
var RESEND_SYNC_STALE_MS = 3 * 6e4;
var RESEND_SYNC_QUEUED_STALE_MS = 6 * 36e5;
function resendSyncActive(s, now = Date.now()) {
  if (!s || s.status !== "queued" && s.status !== "running") return false;
  const beat = s.status === "queued" ? s.queuedAt : s.heartbeatAt ?? s.queuedAt;
  return now - new Date(beat).getTime() < (s.status === "queued" ? RESEND_SYNC_QUEUED_STALE_MS : RESEND_SYNC_STALE_MS);
}
var REPORTS_GRACE_MS = 60 * 6e4;
function campaignReportsMissing(c, now = Date.now()) {
  if (c.sentCount < 20) return false;
  const since = c.completedAt ?? c.startedAt;
  if (!since || now - new Date(since).getTime() < REPORTS_GRACE_MS) return false;
  return c.deliveredCount + c.bouncedCount < c.sentCount / 2;
}
var emptyAudience = () => ({ include: { segmentIds: [], listIds: [], tagIds: [], contactIds: [] }, exclude: { segmentIds: [], listIds: [], tagIds: [] } });

// src/ai.ts
import { z as z17 } from "zod";
var AI_PROVIDERS = ["anthropic", "openai"];
var AI_PROVIDER_LABELS = { anthropic: "Claude (Anthropic)", openai: "OpenAI" };
var modelRef = z17.string().trim().regex(/^(anthropic|openai):[\w.\-:/]+$/, "Choose a model").max(120);
function parseModelRef(ref2) {
  const i = ref2.indexOf(":");
  const provider = ref2.slice(0, i);
  return i > 0 && AI_PROVIDERS.includes(provider) ? { provider, model: ref2.slice(i + 1) } : null;
}
var AI_TONES = ["friendly", "professional", "excited", "formal", "casual"];
var AI_LENGTHS = ["short", "medium", "long"];
var aiWriteEmailSchema = z17.object({
  model: modelRef.optional(),
  /** what the email is about, in the user's words */
  brief: z17.string().trim().min(3, "Describe what the email is about").max(4e3),
  tone: z17.enum(AI_TONES).default("friendly"),
  length: z17.enum(AI_LENGTHS).default("medium"),
  /** where the button should go; no button when empty */
  buttonUrl: z17.string().trim().max(2e3).optional(),
  agentId: z17.string().uuid().nullable().optional()
});
var aiEmailContentSchema = z17.object({
  subject: z17.string().max(500),
  preheader: z17.string().max(300),
  blocks: z17.array(z17.record(z17.string(), z17.unknown())).max(200)
});
var aiReviseEmailSchema = z17.object({
  model: modelRef.optional(),
  instruction: z17.string().trim().min(2, "Say what to change").max(4e3),
  content: aiEmailContentSchema,
  agentId: z17.string().uuid().nullable().optional()
});
var AI_REVISE_SUGGESTIONS = ["Make it shorter", "Make it friendlier", "Make it more professional", "Fix spelling and grammar", "Add a stronger call to action", "Make the subject line catchier"];
var aiSettingsSchema = z17.object({ defaultModel: modelRef.nullable() });

// src/verification.ts
import { z as z18 } from "zod";
var VERIFY_VENDORS = ["auto", "builtin", "zerobounce", "emailable", "myemailverifier"];
var verifyContactsSchema = z18.object({
  selection: bulkSelectionSchema,
  vendor: z18.enum(VERIFY_VENDORS).default("auto"),
  /** skip contacts already VALID/INVALID; re-check only UNVERIFIED/UNKNOWN/CATCH_ALL */
  onlyUnverified: z18.boolean().default(true),
  /** re-verify every contact in these statuses, even ones already verified (overrides onlyUnverified) */
  statuses: z18.array(z18.enum(EMAIL_STATUSES)).min(1).optional()
});
var verifySingleSchema = z18.object({ vendor: z18.enum(VERIFY_VENDORS).default("auto") });
function parseEmailList(text2) {
  const found = text2.toLowerCase().match(/[^\s,;:<>()[\]"'`]+@[^\s,;:<>()[\]"'`]+\.[^\s,;:<>()[\]"'`.]+/g) ?? [];
  return [...new Set(found.map((e) => e.replace(/^mailto:/, "").replace(/\.+$/, "")))];
}
var SENDABLE_STATUSES = ["VALID", "CATCH_ALL", "UNKNOWN", "UNVERIFIED", "INVALID", "DISPOSABLE"];
var DEFAULT_SEND_STATUSES = ["VALID", "CATCH_ALL", "UNKNOWN", "UNVERIFIED"];
var UNCONFIRMED_STATUSES = ["UNKNOWN", "CATCH_ALL"];
var REMOVABLE_STATUSES = ["INVALID", "DISPOSABLE", "SUPPRESSED"];
var REMOVE_BAD_EMAILS_BATCH = 200;
var removeBadEmailsSchema = z18.object({
  statuses: z18.array(z18.enum(REMOVABLE_STATUSES)).min(1),
  /** false = only count */
  apply: z18.boolean().default(false)
});
var MARK_INVALID_BATCH = 1e3;
var markInvalidSchema = z18.object({
  emails: z18.array(z18.string().trim().toLowerCase().min(3).max(320)).min(1).max(MARK_INVALID_BATCH),
  /** false = only report what would change */
  apply: z18.boolean().default(false),
  /** the addresses bounced: also put every one on the suppression list, so nothing ever emails them again */
  block: z18.boolean().default(false)
});

// src/dedupe.ts
import { z as z19 } from "zod";
var listDuplicatesSchema = paginationSchema.extend({
  objectType: z19.enum(["CONTACT", "ORGANIZATION"]).default("CONTACT"),
  status: z19.enum(["PENDING", "MERGED", "DISMISSED", "all"]).default("PENDING"),
  /** only pairs with exactly these reasons (comma-separated) */
  reasons: z19.string().optional()
});
var dismissByReasonsSchema = z19.object({
  objectType: z19.enum(["CONTACT", "ORGANIZATION"]),
  reasons: z19.array(z19.string().min(1)).min(1)
});
var dismissCandidatesSchema = z19.object({
  ids: z19.array(z19.string().min(1)).min(1).max(1e3)
});
var resetDuplicateScanSchema = z19.object({ includeDismissed: z19.boolean().optional() });
var mergeChoicesSchema = z19.record(z19.string(), z19.enum(["a", "b"]));
var mergeCandidateSchema = z19.object({
  /** which side remains (the other is retired into it) */
  survivor: z19.enum(["a", "b"]),
  fields: mergeChoicesSchema.optional()
});
var mergeRecordsSchema = z19.object({
  objectType: z19.enum(["CONTACT", "ORGANIZATION"]),
  survivorId: z19.string().uuid(),
  loserId: z19.string().uuid(),
  /** values keyed by field key: 'survivor' keeps the survivor's, 'loser' takes the loser's */
  fields: z19.record(z19.string(), z19.enum(["survivor", "loser"])).optional()
});
var REASON_LABELS = {
  same_email: "same email address",
  same_phone: "same phone number",
  same_name_org: "same name and organization",
  same_name_domain: "same name and email domain",
  same_name: "same name",
  similar_name_org: "similar name, same or related organization",
  similar_email: "similar email address",
  same_org_name: "same organization name",
  similar_org_name: "similar organization name",
  same_domain: "same domain"
};
var massMergeOptionsSchema = z19.object({
  /** groups whose records hold different values: leave them for review, or merge keeping the oldest record's value */
  conflicts: z19.enum(["skip", "keep_oldest"]).default("skip")
});
var MERGE_SELECTED_TIER = 0;
var mergeSelectedSchema = z19.object({
  ids: z19.array(z19.string().min(1)).min(1).max(1e3)
});
var startMassMergeSchema = massMergeOptionsSchema.extend({ tier: z19.union([z19.literal(1), z19.literal(2), z19.literal(3), z19.literal(4)]) });

// src/imports.ts
import { z as z20 } from "zod";
var CONTACT_PSEUDO_FIELDS = [
  { key: "organization_name", label: "Organization (name)", description: "Finds or creates the organization by name; map more columns to link more organizations" },
  { key: "organization_domain", label: "Organization (domain)", description: "Finds or creates the organization by domain" },
  { key: "tags", label: "Tags", description: "Comma-separated tag names; created when missing" },
  { key: "unsubscribed", label: "Unsubscribed", description: "TRUE/yes/1 unsubscribes the contact; an import never re-subscribes anyone" },
  { key: "notes", label: "Notes", description: "Each non-empty cell becomes a note on the contact" },
  { key: "photo", label: "Profile photo (image URL)", description: "The image is downloaded and stored; existing photos are kept" },
  { key: "full_name", label: "Full name (split into first and last name)", description: 'First word = first name, the rest = last name; "Doe, Jane" and titles like Dr. are handled' }
];
var IMPORT_PERSON_KEYS = ["email", "first_name", "last_name", "full_name"];
var IMPORT_LOGO_LABEL = "Logo (image URL)";
var IMPORT_NEW_FIELD = "__new_field__";
var IMPORT_SEGMENTS = "__segments__";
var IMPORT_SPECIAL_TARGETS = [IMPORT_NEW_FIELD, IMPORT_SEGMENTS];
var IMPORT_NOTES = "notes";
var IMPORT_REPEATABLE_TARGETS = [...IMPORT_SPECIAL_TARGETS, IMPORT_NOTES];
var IMPORT_CONTACT_MULTI_TARGETS = ["email", "phone", "mobile"];
var IMPORT_CONTACT_ORGANIZATION_TARGETS = ["organization_name"];
var IMPORT_CONTACT_GROUP_TARGETS = ["email_label", "phone_label", "organization_domain", "organization_logo", "organization_primary", "organization_role"];
var isImportGroupTarget = (key) => IMPORT_CONTACT_GROUP_TARGETS.includes(key) || key.startsWith(ORGANIZATION_FIELD_PREFIX);
function importColumnGroup(header) {
  return /^(.+\[\d+\])(?:\.|$)/.exec(header)?.[1] ?? "";
}
var IMPORT_CONTACT_ITEM_TARGETS = [
  { key: "email_label", label: "Email label", group: "record", description: "Label (Work, Personal\u2026) of the email in the same item, e.g. emails[1].label" },
  { key: "phone_label", label: "Phone label", group: "record", description: "Label (Mobile, WhatsApp\u2026) of the phone in the same item, e.g. phones[1].label" },
  { key: "organization_primary", label: "Primary organization (true/false)", group: "organization", description: "The organization marked true becomes the contact\u2019s primary one" },
  { key: "organization_role", label: "Job title at this organization", group: "organization", description: "The primary organization\u2019s job title fills the contact\u2019s job title when it has none" }
];
function suggestColumnLabel(header, target) {
  const h = header.toLowerCase();
  if (target !== "mobile" && target !== "email" && /whats\s?app/.test(h)) return "WhatsApp";
  if (target === "mobile" || /mobile|cell/.test(h)) return target === "email" ? null : "Mobile";
  if (/home/.test(h)) return target === "email" ? "Personal" : "Home";
  if (/personal|private/.test(h)) return target === "email" ? "Personal" : "Home";
  if (/work|direct|office|corporate|company|business/.test(h)) return "Work";
  if (/other|secondary|alternat/.test(h)) return "Other";
  return null;
}
var IMPORT_NEW_FIELD_TYPES = ["TEXT", "TEXTAREA", "SELECT", "MULTISELECT", "LIST", "NUMBER", "DATE", "DATETIME", "BOOLEAN", "EMAIL", "URL", "PHONE"];
var IMPORT_NEW_FIELD_TYPE_LABELS = {
  TEXT: "Text",
  TEXTAREA: "Long text",
  SELECT: "Single select",
  MULTISELECT: "Multiple select (comma-separated)",
  LIST: "List of any values (comma-separated)",
  NUMBER: "Number",
  DATE: "Date",
  DATETIME: "Date & time",
  BOOLEAN: "Yes / no",
  EMAIL: "Email",
  URL: "URL",
  PHONE: "Phone"
};
var IMPORT_MAX_SELECT_OPTIONS = 500;
var ORGANIZATION_FIELD_PREFIX = "organization.";
var ORGANIZATION_MATCH_KEYS = ["organization_name", "organization_domain", `${ORGANIZATION_FIELD_PREFIX}website`];
var IMPORT_DUPLICATE_MODES = ["merge", "replace", "skip"];
var importMappingSchema = z20.object({
  /** source column header → target field key (or null to ignore) */
  mapping: z20.record(z20.string(), z20.string().nullable()),
  options: z20.object({
    /**
     * a row matching an existing record (contacts by email, organizations by domain or name): "merge" fills its
     * empty fields, "replace" overwrites them with the row's values (empty cells never clear anything), "skip"
     * leaves it untouched and imports only new records
     */
    onDuplicate: z20.enum(IMPORT_DUPLICATE_MODES).default("merge"),
    tagIds: z20.array(z20.string().uuid()).optional(),
    listId: z20.string().uuid().nullable().optional(),
    /** segment every imported contact is added to by hand (kept whatever the segment's rules say) */
    segmentId: z20.string().uuid().nullable().optional(),
    source: z20.string().trim().max(60).optional(),
    /** type for each column mapped to IMPORT_NEW_FIELD (defaults to the type suggested at upload) */
    newFieldTypes: z20.record(z20.string(), z20.enum(IMPORT_NEW_FIELD_TYPES)).optional(),
    /** label for each column mapped to email, phone or mobile on contact imports (e.g. "Work"); blank = none */
    columnLabels: z20.record(z20.string(), z20.string().trim().max(40)).optional()
  }).default({ onDuplicate: "merge" })
});
var rerunImportSchema = z20.object({ scope: z20.enum(["all", "problems"]) });
var listImportRowsSchema = paginationSchema.extend({
  /** "warnings": imported rows that left out a field value or image (their `error` says what) */
  result: z20.enum(["all", "created", "merged", "updated", "skipped", "error", "warnings"]).default("all")
});
function importTargetLabel(target) {
  return target.group === "organization" ? `Organization \xB7 ${target.label}` : target.label;
}

// src/name-cleanup.ts
import { z as z21 } from "zod";
var CLEANUP_FIELDS = ["firstName", "lastName", "companyName"];
var CLEANUP_FIELD_LABELS = {
  firstName: "First name",
  lastName: "Last name",
  companyName: "Company name"
};
var CLEANUP_CASES = ["title", "sentence", "upper", "lower", "none"];
var DEFAULT_KEEP_WORDS = [
  "LLC",
  "LLP",
  "PLC",
  "GmbH",
  "AG",
  "SA",
  "SARL",
  "SRL",
  "BV",
  "NV",
  "AB",
  "KG",
  "Ltd",
  "Inc",
  "Corp",
  "UK",
  "USA",
  "EU",
  "UAE",
  "IT",
  "AI",
  "HR",
  "PR",
  "II",
  "III",
  "IV"
];
var cleanupFieldOptionsSchema = z21.object({
  enabled: z21.boolean().default(false),
  case: z21.enum(CLEANUP_CASES).default("title"),
  /** only change values written entirely in capitals or entirely in lowercase (leaves "McDonald", "eBay" alone) */
  onlyUniform: z21.boolean().default(true),
  /** trim the ends and turn runs of spaces into one */
  fixSpaces: z21.boolean().default(true),
  /** title case: "van", "de", "von"… stay lowercase unless they start the value (names) */
  lowerParticles: z21.boolean().default(false),
  /** title case: "of", "and", "the"… stay lowercase unless they start the value (companies) */
  lowerSmallWords: z21.boolean().default(false)
});
var nameCleanupSchema = z21.object({
  fields: z21.object({
    firstName: cleanupFieldOptionsSchema.optional(),
    lastName: cleanupFieldOptionsSchema.optional(),
    companyName: cleanupFieldOptionsSchema.optional()
  }),
  keepWords: z21.array(z21.string().trim().min(1).max(40)).max(200).default(DEFAULT_KEEP_WORDS)
});
var nameCleanupApplySchema = nameCleanupSchema.extend({
  /** which records this batch covers: contacts (first/last name) or organizations (company name) */
  target: z21.enum(["contacts", "organizations"]),
  /** keyset cursor: the last id of the previous batch */
  after: z21.string().nullish()
});
var PARTICLES = /* @__PURE__ */ new Set(["van", "von", "der", "den", "de", "del", "della", "di", "da", "du", "des", "la", "le", "dos", "das", "ter", "ten", "bin", "ibn", "al"]);
var SMALL_WORDS = /* @__PURE__ */ new Set(["a", "an", "and", "as", "at", "by", "for", "in", "of", "on", "or", "the", "to", "with", "und", "et"]);
var hasLetters = (s) => /\p{L}/u.test(s);
var upperFirst = (s) => s ? s[0].toLocaleUpperCase() + s.slice(1) : s;
function capitalizeWord(word, personName) {
  const lower = word.toLocaleLowerCase();
  let out = "";
  let start = true;
  for (let i = 0; i < lower.length; i++) {
    const ch = lower[i];
    out += start && /\p{L}/u.test(ch) ? ch.toLocaleUpperCase() : ch;
    if (/\p{L}|\p{N}/u.test(ch)) start = false;
    else if (ch === "-" || ch === "." || ch === "(" || ch === "/" || ch === '"') start = true;
    else if (ch === "'" || ch === "\u2019") start = i === 1 || i >= 2 && !/\p{L}/u.test(lower[i - 2]);
  }
  if (personName && /^mc\p{L}{2,}/u.test(lower)) out = "Mc" + out[2].toLocaleUpperCase() + out.slice(3);
  return out;
}
function cleanName(value, field, opts, keepWords = DEFAULT_KEEP_WORDS) {
  let v = value;
  if (opts.fixSpaces) v = v.replace(/\s+/g, " ").trim();
  if (!v) return value;
  if (opts.case === "none" || !hasLetters(v)) return v;
  if (opts.onlyUniform && v !== v.toLocaleUpperCase() && v !== v.toLocaleLowerCase()) return v;
  if (opts.case === "upper") return v.toLocaleUpperCase();
  if (opts.case === "lower") return v.toLocaleLowerCase();
  const keep = new Map(keepWords.map((w) => [w.toLocaleLowerCase(), w]));
  const personName = field !== "companyName";
  let first = true;
  return v.split(/(\s+)/).map((part2) => {
    if (!part2 || /^\s+$/.test(part2)) return part2;
    const lower = part2.toLocaleLowerCase();
    const bare2 = lower.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "");
    const isFirst = first;
    first = false;
    const kept = keep.get(bare2);
    if (kept) return lower.replace(bare2, kept);
    if (opts.case === "sentence") return isFirst ? upperFirst(lower) : lower;
    if (!isFirst && opts.lowerParticles && personName && PARTICLES.has(bare2)) return lower;
    if (!isFirst && opts.lowerSmallWords && SMALL_WORDS.has(bare2)) return lower;
    return capitalizeWord(part2, personName);
  }).join("");
}

// src/contact-checks.ts
import { z as z22 } from "zod";
var CONTACT_ISSUES = {
  missing_name: { label: "No name", severity: "high", description: "Neither a first nor a last name." },
  placeholder_name: { label: "Placeholder name", severity: "high", description: 'A stand-in such as "test", "n/a", "unknown" or "customer".' },
  no_letters: { label: "Name without letters", severity: "high", description: "Only digits, dots, dashes or other symbols." },
  email_in_name: { label: "Email or link in name", severity: "high", description: "The name field holds an email address or a web address." },
  digits_in_name: { label: "Digits in name", severity: "medium", description: "Real names rarely contain numbers." },
  company_in_name: { label: "Company in name", severity: "medium", description: "Looks like a company (Ltd, LLC, Group, Bank\u2026) rather than a person." },
  full_name_in_first: { label: "Full name in first name", severity: "medium", description: "First name holds several words and the last name is empty." },
  same_first_last: { label: "Same first and last name", severity: "medium", description: "First and last name are identical." },
  gibberish_name: { label: "Gibberish name", severity: "medium", description: "Keyboard mashing, a repeated letter or no vowels at all." },
  mixed_scripts: { label: "Mixed alphabets", severity: "medium", description: "Latin mixed with Greek or Cyrillic letters in one word, often a typo." },
  no_contact_info: { label: "No email or phone", severity: "medium", description: "There is no way to reach this contact." },
  invalid_phone: { label: "Odd phone number", severity: "medium", description: "Contains letters, or has too few or too many digits." },
  invalid_field_value: { label: "Value does not fit the field", severity: "medium", description: "For example text in a number field or an email field without an @." },
  title_in_name: { label: "Title in name", severity: "low", description: "Mr, Mrs, Dr and similar belong outside the name." },
  initial_only: { label: "Initial only", severity: "low", description: "A single letter instead of a name." },
  name_too_long: { label: "Very long name", severity: "low", description: "Over 40 characters or more than 4 words." },
  odd_capitalization: { label: "Odd capitals", severity: "low", description: "ALL CAPITALS, all lowercase or capitals in odd places. Fix them with Name clean up." },
  odd_job_title: { label: "Odd job title", severity: "low", description: "The job title holds an email, a link or a phone number." },
  placeholder_value: { label: "Placeholder value", severity: "low", description: 'A field holds a stand-in such as "n/a", "-", "null" or "#N/A".' }
};
var CONTACT_ISSUE_CODES = Object.keys(CONTACT_ISSUES);
var oddContactsScanSchema = z22.object({
  /** keyset cursor: the last id of the previous batch */
  after: z22.string().nullish(),
  /** re-check just these contacts (after editing or bulk actions) */
  ids: z22.array(z22.string()).max(1e4).optional()
});
var PLACEHOLDER_NAMES = /* @__PURE__ */ new Set([
  "test",
  "testing",
  "tester",
  "test test",
  "asdf",
  "qwerty",
  "n/a",
  "na",
  "n.a.",
  "none",
  "null",
  "nil",
  "undefined",
  "unknown",
  "nobody",
  "anonymous",
  "anon",
  "noname",
  "no name",
  "first",
  "firstname",
  "first name",
  "last",
  "lastname",
  "last name",
  "name",
  "fname",
  "lname",
  "surname",
  "customer",
  "client",
  "user",
  "guest",
  "contact",
  "lead",
  "sir",
  "madam",
  "sir/madam",
  "sir or madam",
  "xxx",
  "xx",
  "x",
  "abc",
  "aaa",
  "dummy",
  "sample",
  "example",
  "demo",
  "fake",
  "info",
  "admin",
  "sales",
  "support",
  "office",
  "hello",
  "team",
  "mr",
  "mrs",
  "ms",
  "tbd",
  "tba",
  "todo",
  "blank",
  "empty",
  "other",
  "nn",
  "-",
  "--",
  ".",
  "?",
  "??",
  "#n/a",
  "0"
]);
var PLACEHOLDER_VALUES = /* @__PURE__ */ new Set(["n/a", "na", "n.a.", "none", "null", "nil", "undefined", "unknown", "-", "--", "---", ".", "..", "?", "??", "#n/a", "#value!", "#ref!", "#name?", "tbd", "tba", "xxx", "test", "nan"]);
var COMPANY_WORDS = /* @__PURE__ */ new Set([
  "ltd",
  "ltd.",
  "limited",
  "llc",
  "l.l.c.",
  "inc",
  "inc.",
  "incorporated",
  "corp",
  "corp.",
  "corporation",
  "gmbh",
  "plc",
  "llp",
  "co.",
  "company",
  "group",
  "holdings",
  "holding",
  "solutions",
  "services",
  "consulting",
  "consultants",
  "trading",
  "enterprises",
  "industries",
  "international",
  "technologies",
  "technology",
  "systems",
  "partners",
  "associates",
  "agency",
  "bank",
  "hotel",
  "hotels",
  "restaurant",
  "studio",
  "studios",
  "shop",
  "store",
  "foundation",
  "university",
  "school",
  "clinic",
  "ministry",
  "s.a.",
  "srl",
  "b.v.",
  "bv",
  "ag",
  "sarl",
  "pty",
  "properties",
  "realty",
  "estates",
  "investments",
  "capital",
  "ventures",
  "media",
  "marketing",
  "logistics",
  "insurance"
]);
var TITLES = /* @__PURE__ */ new Set(["mr", "mr.", "mrs", "mrs.", "ms", "ms.", "miss", "mx", "dr", "dr.", "prof", "prof.", "eng", "eng.", "sir", "dame", "rev", "rev."]);
var KEYBOARD_RUNS = ["qwert", "asdf", "zxcv", "sdfg", "dfgh", "fghj", "hjkl", "xcvb", "cvbn", "vbnm"];
var EMAIL_RE = /[^\s@]+@[^\s@]+\.[^\s@]+/;
var URL_RE = /(https?:\/\/|www\.)|\b[a-z0-9-]+\.(com|net|org|io|co|uk|gr|cy|de|info|biz)\b/i;
var words = (s) => s.split(/\s+/).filter(Boolean);
var bare = (w) => w.toLocaleLowerCase().replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}.]+$/gu, "");
var digitCount = (s) => (s.match(/\d/g) ?? []).length;
function isGibberish(word) {
  const w = word.toLowerCase();
  if (/(\p{L})\1{3,}/u.test(w)) return true;
  if (KEYBOARD_RUNS.some((r) => w.includes(r))) return true;
  return /^[a-z]{5,}$/.test(w) && !/[aeiouy]/.test(w);
}
function hasMixedScripts(word) {
  const latin = /\p{Script=Latin}/u.test(word);
  return latin && (/\p{Script=Greek}/u.test(word) || /\p{Script=Cyrillic}/u.test(word));
}
function oddCaps(name) {
  const letters = name.replace(/[^\p{L}]/gu, "");
  if (letters.length < 2) return false;
  if (name === name.toLocaleUpperCase() || name === name.toLocaleLowerCase()) return true;
  return words(name).some((w) => /^\p{Ll}/u.test(w) && /\p{Lu}/u.test(w.slice(1)));
}
function checkName(value, label, add) {
  const v = value.trim();
  const lower = v.toLocaleLowerCase();
  if (PLACEHOLDER_NAMES.has(lower)) return add("placeholder_name", label, v);
  if (!/\p{L}/u.test(v)) return add("no_letters", label, v);
  if (EMAIL_RE.test(v) || URL_RE.test(v)) return add("email_in_name", label, v);
  if (/\d/.test(v)) add("digits_in_name", label, v);
  const ws = words(v);
  if (ws.some((w) => TITLES.has(bare(w)) || TITLES.has(w.toLocaleLowerCase()))) add("title_in_name", label, v);
  if (ws.some((w) => isGibberish(w))) add("gibberish_name", label, v);
  if (ws.some((w) => hasMixedScripts(w))) add("mixed_scripts", label, v);
  if (v.replace(/[^\p{L}]/gu, "").length === 1) add("initial_only", label, v);
  if (v.length > 40 || ws.length > 4) add("name_too_long", label, v);
  if (oddCaps(v)) add("odd_capitalization", label, v);
}
function fieldValueFits(type, value) {
  if (value === null || value === void 0 || value === "") return true;
  const s = typeof value === "string" ? value.trim() : value;
  switch (type) {
    case "EMAIL":
      return typeof s === "string" && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
    case "URL":
      return typeof s === "string" && /^(https?:\/\/)?[^\s/]+\.[^\s/]{2,}(\/\S*)?$/i.test(s);
    case "NUMBER":
    case "CURRENCY":
      return typeof s === "number" || typeof s === "string" && /^-?[\d\s,.]+$/.test(s) && /\d/.test(s);
    case "PHONE":
      return typeof s === "string" && !/\p{L}/u.test(s.replace(/\b(ext|x)\b/gi, "")) && digitCount(s) >= 7 && digitCount(s) <= 15;
    case "DATE":
    case "DATETIME":
      return typeof s === "string" && !Number.isNaN(Date.parse(s));
    default:
      return true;
  }
}
function checkContact(c, defs = []) {
  const issues = [];
  const seen = /* @__PURE__ */ new Set();
  const add = (code, field, value) => {
    const key = `${code}:${field ?? ""}`;
    if (seen.has(key)) return;
    seen.add(key);
    issues.push({ code, ...field ? { field } : {}, ...value !== void 0 ? { value } : {} });
  };
  const first = c.firstName?.trim() || "";
  const last = c.lastName?.trim() || "";
  if (!first && !last) add("missing_name");
  if (first) checkName(first, "First name", add);
  if (last) checkName(last, "Last name", add);
  const full = `${first} ${last}`.trim();
  if (full && words(full).some((w) => COMPANY_WORDS.has(w.toLocaleLowerCase()))) add("company_in_name", "Name", full);
  if (first && !last && words(first).length >= 2 && !seen.has("placeholder_name:First name")) add("full_name_in_first", "First name", first);
  if (first && last && first.toLocaleLowerCase() === last.toLocaleLowerCase()) add("same_first_last", "Name", full);
  const phones = c.phones.map((p) => p.trim()).filter(Boolean);
  if (!c.email && !phones.length) add("no_contact_info");
  for (const p of phones) if (!fieldValueFits("PHONE", p)) add("invalid_phone", "Phone", p);
  const job = c.jobTitle?.trim();
  if (job) {
    if (PLACEHOLDER_VALUES.has(job.toLocaleLowerCase())) add("placeholder_value", "Job title", job);
    else if (EMAIL_RE.test(job) || URL_RE.test(job) || digitCount(job) >= 7) add("odd_job_title", "Job title", job);
  }
  for (const d of defs) {
    const raw = c.fields[d.key];
    if (raw === null || raw === void 0 || raw === "") continue;
    if (typeof raw === "string" && PLACEHOLDER_VALUES.has(raw.trim().toLocaleLowerCase())) {
      add("placeholder_value", d.label, raw);
      continue;
    }
    if (!fieldValueFits(d.type, raw)) add("invalid_field_value", d.label, typeof raw === "string" ? raw : JSON.stringify(raw));
  }
  return issues;
}

// src/contact-info.ts
import { z as z23 } from "zod";
var noContactInfoQuerySchema = paginationSchema.pick({ page: true, pageSize: true, q: true });
var noContactInfoFieldsSchema = z23.object({ fields: z23.array(z23.string().trim().min(1).max(100)).min(1, "Pick at least one field").max(500).nullable() });

// src/channel-cleaner.ts
import { z as z24 } from "zod";
var channelCleanerScanSchema = z24.object({
  after: z24.string().nullish(),
  ids: z24.array(z24.string()).max(1e4).optional()
});
var channelCleanerApplySchema = z24.object({
  items: z24.array(
    z24.object({
      contactId: z24.string(),
      field: z24.string().min(1).max(200),
      kind: z24.enum(["email", "phone"]),
      value: z24.string().min(1).max(320),
      label: z24.string().trim().max(40).nullish()
    })
  ).min(1).max(500),
  /** take the value out of the field it was found in (otherwise it is only copied) */
  removeFromSource: z24.boolean().default(true)
});
var EMAIL_RE2 = /(?:mailto:)?([a-z0-9._%+'-]+@[a-z0-9.-]+\.[a-z]{2,})/gi;
var PHONE_RE = /(?<![\p{L}\d])(?:\+|00)?\(?\d[\d\s().-]{5,}\d(?:\s*(?:ext\.?|x)\s*\d{1,5})?(?![\p{L}\d])/giu;
var DATE_RE = /^\d{1,4}[.-]\d{1,2}[.-]\d{1,4}$/;
var SKIP_TYPES = /* @__PURE__ */ new Set(["BOOLEAN", "DATE", "DATETIME"]);
var FREE_MAIL = /* @__PURE__ */ new Set([
  "gmail.com",
  "googlemail.com",
  "yahoo.com",
  "yahoo.co.uk",
  "yahoo.gr",
  "ymail.com",
  "hotmail.com",
  "hotmail.co.uk",
  "hotmail.gr",
  "outlook.com",
  "live.com",
  "msn.com",
  "icloud.com",
  "me.com",
  "mac.com",
  "aol.com",
  "gmx.com",
  "gmx.de",
  "gmx.net",
  "web.de",
  "mail.com",
  "mail.ru",
  "yandex.ru",
  "yandex.com",
  "protonmail.com",
  "proton.me",
  "zoho.com",
  "cytanet.com.cy",
  "primehome.com",
  "otenet.gr",
  "windowslive.com",
  "qq.com",
  "163.com"
]);
var digits = (s) => s.replace(/\D/g, "");
var samePhoneNumber = (a, b) => {
  const [x, y] = [digits(a), digits(b)];
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  return short.length >= 7 && long.endsWith(short.replace(/^0+/, ""));
};
var isPhoneFieldName = (s) => /phone|mobile|whats\s?app|(^|[^a-z])(tel|cell|fax|gsm)([^a-z]|$)|κινητ|τηλ/i.test(s);
function labelBefore(kind, text2, value) {
  const i = text2.toLowerCase().indexOf(value.toLowerCase());
  const before = i > 0 ? text2.slice(Math.max(0, i - 20), i).toLowerCase() : "";
  const word = before.match(/([\p{L}-]+)\.?\s*[:.-]?\s*$/u)?.[1];
  if (!word) return null;
  if (/^(whatsapp|wa)$/.test(word)) return kind === "phone" ? "WhatsApp" : null;
  if (/^(mobile|mob|cell|gsm|viber|m|κινητό)$/.test(word)) return kind === "phone" ? "Mobile" : null;
  if (/^(office|work|business|direct|w)$/.test(word)) return "Work";
  if (/^(home|h)$/.test(word)) return kind === "phone" ? "Home" : "Personal";
  if (/^(fax|f)$/.test(word)) return kind === "phone" ? "Fax" : null;
  if (/^(personal|private)$/.test(word)) return kind === "phone" ? "Home" : "Personal";
  return null;
}
function suggestChannelLabel(kind, fieldLabel, value) {
  const l = fieldLabel.toLowerCase();
  if (/\bwhats\s?app\b/.test(l)) return kind === "phone" ? "WhatsApp" : "Other";
  if (/\b(mobile|mob|cell|cellular|gsm|viber)\b|κινητ/.test(l)) return kind === "phone" ? "Mobile" : "Other";
  if (/\bfax\b/.test(l)) return kind === "phone" ? "Fax" : "Other";
  if (/\b(work|office|business|company|corporate|direct)\b/.test(l)) return "Work";
  if (/\b(home|private|personal)\b/.test(l)) return kind === "phone" ? "Home" : "Personal";
  if (/e-?mail|phone|tel|number|\bno\b/i.test(fieldLabel)) {
    const rest = fieldLabel.replace(/\b(e-?mail|mail|address|phone|telephone|tel|number|no|nr|num|contact|primary|main|other|secondary|alt|alternative|1|2|3)\b|[#.:_()-]/gi, " ").replace(/\s+/g, " ").trim();
    if (rest && rest.length <= 20 && !/\d/.test(rest)) return rest.charAt(0).toUpperCase() + rest.slice(1).toLowerCase();
  }
  if (kind === "email") return FREE_MAIL.has(value.split("@")[1] ?? "") ? "Personal" : "Work";
  return "Other";
}
function splitRun(run) {
  if (digits(run).length <= 15) return [run];
  for (let i = run.indexOf(" "); i > 0; i = run.indexOf(" ", i + 1)) {
    const [a, b] = [run.slice(0, i).trim(), run.slice(i + 1).trim()];
    const [da, db] = [digits(a).length, digits(b).length];
    if (da >= 7 && da <= 15 && db >= 7) return [a, ...splitRun(b)];
  }
  return [];
}
function findEmails(text2) {
  const out = [];
  for (const m of text2.matchAll(EMAIL_RE2)) {
    const email = m[1].toLowerCase().replace(/\.+$/, "");
    if (!out.includes(email)) out.push(email);
  }
  return out;
}
function findPhones(text2) {
  const out = [];
  for (const p of phoneMatches(text2)) if (!out.some((x) => samePhoneNumber(x, p))) out.push(p);
  return out;
}
function phoneMatches(text2) {
  const out = [];
  const withoutEmails = text2.replace(EMAIL_RE2, " ");
  for (const m of withoutEmails.matchAll(PHONE_RE)) {
    for (const part2 of splitRun(m[0])) {
      const p = part2.trim().replace(/^\((?=[^)]*$)/, "").replace(/[\s.(-]+$/, "");
      const n = digits(p.replace(/\s*(ext\.?|x)\s*\d+$/i, "")).length;
      if (n < 7 || n > 15) continue;
      if (DATE_RE.test(p) || /^\d+\.\d+$/.test(p)) continue;
      out.push(p);
    }
  }
  return out;
}
var LABEL_WORDS = "e-?mail|mail|tel|telephone|phone|mob|mobile|cell|gsm|fax|office|work|home|direct|whats\\s?app|wa|viber|t|m|e|f|p|w|h";
function removeValues(text2, values) {
  let out = text2;
  const forms = values.flatMap((v) => v.includes("@") ? [v] : [v, ...phoneMatches(text2).filter((p) => p !== v && samePhoneNumber(p, v))]).sort((a, b) => b.length - a.length);
  for (const v of forms) {
    const escaped = v.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    out = out.replace(new RegExp(`(\\b(${LABEL_WORDS})\\.?\\s*[:.-]?\\s*)?(mailto:)?${escaped}`, "gi"), " ");
  }
  const leftover = out.replace(new RegExp(`\\b(${LABEL_WORDS}|email address)\\b`, "gi"), "").replace(/[\s,;|/:<>()[\]&+.-]/g, "");
  if (!leftover) return null;
  return out.replace(/<\s*>|\(\s*\)|\[\s*\]/g, " ").replace(/\s*([,;|/])\s*(?:[,;|/]\s*)+/g, "$1 ").replace(/(\s[-–]\s)(\s*[-–](?=\s))+/g, "$1").replace(/[ \t]+/g, " ").replace(/^[\s,;|/:-]+|[\s,;|/:-]+$/g, "").trim() || null;
}
function findContactChannels(c, defs = []) {
  const sources = [];
  if (c.firstName) sources.push({ field: "firstName", fieldLabel: "First name", type: "NAME", value: c.firstName });
  if (c.lastName) sources.push({ field: "lastName", fieldLabel: "Last name", type: "NAME", value: c.lastName });
  if (c.jobTitle) sources.push({ field: "jobTitle", fieldLabel: "Job title", type: "TEXT", value: c.jobTitle });
  for (const d of defs) {
    if (SKIP_TYPES.has(d.type)) continue;
    const raw = c.fields[d.key];
    const values = Array.isArray(raw) ? raw : [raw];
    const text2 = values.filter((v) => typeof v === "string" || typeof v === "number" && isPhoneFieldName(`${d.key} ${d.label}`)).join(", ");
    if (text2.trim()) sources.push({ field: d.key, fieldLabel: d.label, type: d.type, value: text2 });
  }
  const knownEmails = new Set(c.emails.map((e) => e.toLowerCase()));
  const knownPhones = [...c.phones];
  const out = [];
  for (const s of sources) {
    const emails = findEmails(s.value);
    const phones = s.type === "PHONE" || s.type !== "URL" && isPhoneFieldName(`${s.field} ${s.fieldLabel}`) ? findPhones(s.value) : [];
    if (!emails.length && !phones.length) continue;
    const remainder = removeValues(s.value, [...emails, ...phones]);
    const base = { field: s.field, fieldLabel: s.fieldLabel, source: s.value, remainder };
    for (const email of emails) {
      out.push({ ...base, kind: "email", value: email, label: labelBefore("email", s.value, email) ?? suggestChannelLabel("email", s.fieldLabel, email), status: knownEmails.has(email) ? "duplicate" : "new" });
      knownEmails.add(email);
    }
    for (const phone of phones) {
      out.push({ ...base, kind: "phone", value: phone, label: labelBefore("phone", s.value, phone) ?? suggestChannelLabel("phone", s.fieldLabel, phone), status: knownPhones.some((p) => samePhoneNumber(p, phone)) ? "duplicate" : "new" });
      knownPhones.push(phone);
    }
  }
  return out;
}
var channelFindingKey = (contactId, f) => `${contactId}|${f.field}|${f.kind}|${f.value}`;

// src/duplicate-phones.ts
import { z as z25 } from "zod";
var duplicatePhonesScanSchema = z25.object({
  after: z25.string().nullish(),
  ids: z25.array(z25.string()).max(1e4).optional()
});
var duplicatePhonesApplySchema = z25.object({
  contactIds: z25.array(z25.string()).min(1).max(500)
});
var digits2 = (s) => s.replace(/\D/g, "");
function isDuplicatePhone(a, b) {
  const [x, y] = [digits2(a), digits2(b)];
  if (!x || !y) return a.trim().toLowerCase() === b.trim().toLowerCase();
  const [short, long] = x.length <= y.length ? [x, y] : [y, x];
  const core = short.replace(/^0+/, "");
  if (core.length < 7) return short === long;
  return long.endsWith(core) && long.length - core.length <= 5;
}
function better(a, b) {
  const [da, db] = [digits2(a.number).replace(/^00/, ""), digits2(b.number).replace(/^00/, "")];
  if (da.length !== db.length) return da.length > db.length;
  const [pa, pb] = [a.number.trim().startsWith("+"), b.number.trim().startsWith("+")];
  if (pa !== pb) return pa;
  return a.isPrimary && !b.isPrimary;
}
function findDuplicatePhones(phones) {
  const groupOf = phones.map((_, i) => i);
  const root = (i) => groupOf[i] === i ? i : groupOf[i] = root(groupOf[i]);
  for (let i = 0; i < phones.length; i++) {
    for (let j = i + 1; j < phones.length; j++) if (isDuplicatePhone(phones[i].number, phones[j].number)) groupOf[root(j)] = root(i);
  }
  const members = /* @__PURE__ */ new Map();
  phones.forEach((_, i) => members.set(root(i), [...members.get(root(i)) ?? [], i]));
  const out = [];
  for (const idx of members.values()) {
    if (idx.length < 2) continue;
    const items = idx.map((i) => phones[i]);
    const best = items.reduce((a, b) => better(b, a) ? b : a);
    out.push({
      keep: {
        number: best.number,
        // the kept entry's label, else the first label in the group
        label: best.label || items.find((p) => p.label)?.label || null,
        isPrimary: items.some((p) => p.isPrimary)
      },
      remove: items.filter((p) => p !== best)
    });
  }
  return out;
}
function dedupePhoneList(phones) {
  const groups = findDuplicatePhones(phones);
  const out = [];
  const placed = /* @__PURE__ */ new Set();
  for (const p of phones) {
    const g = groups.find((x) => x.keep.number === p.number || x.remove.includes(p));
    if (!g) out.push(p);
    else if (!placed.has(g)) {
      placed.add(g);
      out.push(g.keep);
    }
  }
  return out;
}

// src/phone-country-codes.ts
import { z as z26 } from "zod";
import { getCountries, getCountryCallingCode, isSupportedCountry, parsePhoneNumberFromString } from "libphonenumber-js/max";
var countryCodesScanSchema = z26.object({
  after: z26.string().nullish(),
  ids: z26.array(z26.string()).max(1e4).optional()
});
var countryCodesApplySchema = z26.object({
  items: z26.array(
    z26.object({
      contactId: z26.string(),
      number: z26.string().min(1).max(100),
      /** a country picked by hand (ISO code), used instead of the one worked out for the contact */
      country: z26.string().length(2).toUpperCase().nullish(),
      /** take the number off the contact instead of fixing it */
      remove: z26.boolean().optional()
    })
  ).min(1).max(500)
});
var digits3 = (s) => s.replace(/\D/g, "");
var EXT_RE = /\s*(?:ext\.?|x|#)\s*\d{1,5}$/i;
var COUNTRY_FIELD_RE = /country|nation|χώρα/i;
var supported = (code) => !!code && isSupportedCountry(code);
var callingCodeOf = (country) => supported(country) ? `+${getCountryCallingCode(country)}` : null;
var phoneCountries = () => getCountries().map((code) => ({ code, name: matchCountry(code, { codes: true }) ?? code, callingCode: `+${getCountryCallingCode(code)}` })).sort((a, b) => a.name.localeCompare(b.name));
function detectContactCountry(c) {
  const found = (code, source) => supported(code) ? { code, callingCode: callingCodeOf(code), source } : null;
  const fromAddresses = (list) => list.map((a) => a.country ? countryCodeOf(a.country) : null).find(supported) ?? null;
  const address = found(fromAddresses(c.addresses), "address");
  if (address) return address;
  for (const d of c.defs) {
    if (!COUNTRY_FIELD_RE.test(`${d.key} ${d.label}`)) continue;
    const raw = c.fields[d.key];
    const value = (Array.isArray(raw) ? raw : [raw]).find((v) => typeof v === "string" && !!v.trim());
    const field = value ? found(countryCodeOf(value), "field") : null;
    if (field) return field;
  }
  const organization = found(fromAddresses(c.organizationAddresses), "organization");
  if (organization) return organization;
  for (const p of c.phones) {
    const t = p.trim();
    if (!/^(\+|00)/.test(t)) continue;
    const phone = parsePhoneNumberFromString(`+${t.startsWith("+") ? digits3(t) : digits3(t).slice(2)}`);
    if (phone?.isValid() && phone.country) return found(phone.country, "phone");
  }
  return null;
}
function countryCodeFix(number, country) {
  const raw = number.trim();
  const ext = raw.match(EXT_RE)?.[0] ?? "";
  const main = raw.slice(0, raw.length - ext.length).trim();
  if (main.startsWith("+")) return null;
  const d = digits3(main);
  const fail = { status: supported(country) ? "invalid" : "no-country", fixed: null, country: null };
  if (/^\(?00/.test(main)) {
    const phone = parsePhoneNumberFromString(`+${d.slice(2)}`);
    if (!phone?.isPossible()) return { ...fail, status: "invalid" };
    return { status: "plus", fixed: main.replace(/^(\(?)00\s*/, "$1+") + ext, country: phone.country ?? null };
  }
  if (supported(country)) {
    const phone = parsePhoneNumberFromString(main, country);
    if (phone?.isValid()) {
      const cc = phone.countryCallingCode;
      const hadCode = d.startsWith(cc) && phone.nationalNumber === d.slice(cc.length);
      if (hadCode) return { status: "plus", fixed: (/^\d/.test(main) ? `+${main}` : phone.formatInternational()) + ext, country: phone.country ?? country };
      return { status: "add", fixed: phone.formatInternational() + ext, country: phone.country ?? country };
    }
  }
  if (d.length >= 10 && !d.startsWith("0")) {
    const phone = parsePhoneNumberFromString(`+${d}`);
    if (phone?.isValid()) return { status: "plus", fixed: (/^\d/.test(main) ? `+${main}` : phone.formatInternational()) + ext, country: phone.country ?? null };
  }
  return fail;
}
function findCountryCodeFixes(phones, country) {
  const out = [];
  for (const p of phones) {
    const fix = countryCodeFix(p.number, country);
    if (fix) out.push({ number: p.number, label: p.label, ...fix });
  }
  return out;
}
function applyCountryCodes(phones, country, items) {
  const fixed = [];
  const removed = [];
  const next = [];
  for (const p of phones) {
    const item = items.find((i) => i.number === p.number);
    if (item?.remove) {
      removed.push(p.number);
      continue;
    }
    const fix = item ? countryCodeFix(p.number, item.country || country) : null;
    if (fix?.fixed) fixed.push(p.number);
    next.push(fix?.fixed ? { ...p, number: fix.fixed } : p);
  }
  if (next.length && !next.some((p) => p.isPrimary)) next[0] = { ...next[0], isPrimary: true };
  return { phones: next, fixed, removed };
}
var countryCodeFindingKey = (contactId, number) => `${contactId}|${number}`;

// src/asana.ts
import { z as z27 } from "zod";
var ref = (v) => {
  const r = v;
  return r && typeof r.gid === "string" ? { gid: r.gid, name: typeof r.name === "string" ? r.name : "" } : null;
};
var str = (v) => typeof v === "string" ? v : null;
var ASANA_DEFAULT_SECTION = /^\(?no section\)?$|^untitled section$/i;
function parseAsanaExport(json, endOfDay) {
  const root = json;
  const list = Array.isArray(root) ? root : Array.isArray(root?.data) ? root.data : null;
  if (!list) throw new Error('This is not an Asana export: expected a "data" list of tasks.');
  const byGid = /* @__PURE__ */ new Map();
  const add = (raw, parentGid) => {
    const gid = str(raw.gid);
    if (!gid || typeof raw.name !== "string") return;
    if (raw.resource_type && raw.resource_type !== "task") return;
    const existing = byGid.get(gid);
    const membership = Array.isArray(raw.memberships) ? raw.memberships[0] : void 0;
    const project = ref(membership?.project) ?? (Array.isArray(raw.projects) ? ref(raw.projects[0]) : null);
    const sectionName = str(membership?.section?.name);
    const parent = ref(raw.parent)?.gid ?? parentGid;
    const dueOn = str(raw.due_on);
    const task = {
      gid,
      name: raw.name.trim() || "Untitled task",
      notes: str(raw.notes) ?? "",
      assignee: ref(raw.assignee),
      completedAt: raw.completed ? str(raw.completed_at) ?? str(raw.modified_at) ?? (/* @__PURE__ */ new Date()).toISOString() : null,
      dueAt: str(raw.due_at) ?? (dueOn && /^\d{4}-\d{2}-\d{2}$/.test(dueOn) ? endOfDay(dueOn) : null),
      createdAt: str(raw.created_at),
      project,
      section: sectionName && !ASANA_DEFAULT_SECTION.test(sectionName.trim()) ? sectionName.trim() : null,
      parentGid: parent,
      depth: 0
    };
    const compact = !("created_at" in raw) && !("completed" in raw);
    if (!existing) byGid.set(gid, task);
    else if (!compact) byGid.set(gid, { ...task, parentGid: task.parentGid ?? existing.parentGid });
    else if (!existing.parentGid && parent) existing.parentGid = parent;
    for (const sub of Array.isArray(raw.subtasks) ? raw.subtasks : []) add(sub, gid);
  };
  for (const item of list) if (item && typeof item === "object") add(item, null);
  const depthOf = (t, seen = /* @__PURE__ */ new Set()) => {
    const parent = t.parentGid ? byGid.get(t.parentGid) : void 0;
    if (!parent || seen.has(t.gid)) return 0;
    seen.add(t.gid);
    return depthOf(parent, seen) + 1;
  };
  const rootOf = (t) => {
    let cur = t;
    for (let i = 0; i < 50 && cur.parentGid && byGid.has(cur.parentGid); i++) cur = byGid.get(cur.parentGid);
    return cur;
  };
  const tasks = [...byGid.values()].map((t) => {
    const depth = depthOf(t);
    return depth === 0 ? { ...t, parentGid: null, depth } : { ...t, depth, project: rootOf(t).project, section: null };
  });
  tasks.sort((a, b) => a.depth - b.depth);
  const count = (items, key) => {
    const map = /* @__PURE__ */ new Map();
    for (const i of items) if (i) map.set(key(i), { item: i, count: (map.get(key(i))?.count ?? 0) + 1 });
    return [...map.values()];
  };
  return {
    tasks,
    projects: count(tasks.map((t) => t.project), (p) => p.gid).map(({ item, count: n }) => ({ ...item, count: n })),
    assignees: count(tasks.map((t) => t.assignee), (a) => a.gid).map(({ item, count: n }) => ({ ...item, count: n })).sort((a, b) => b.count - a.count),
    sections: count(
      tasks.filter((t) => t.section).map((t) => ({ project: t.project?.gid ?? null, name: t.section })),
      (s) => `${s.project}|${s.name}`
    ).map(({ item, count: n }) => ({ ...item, count: n }))
  };
}
function matchAsanaUser(name, users) {
  const norm = (s) => s.normalize("NFKD").replace(/[̀-ͯ]/g, "").trim().toLowerCase();
  const n = norm(name);
  if (!n) return void 0;
  const exact = users.find((u) => norm(u.name) === n);
  if (exact) return exact;
  const first = n.split(/\s+/)[0];
  const sameFirst = users.filter((u) => norm(u.name).split(/\s+/)[0] === first);
  return sameFirst.length === 1 ? sameFirst[0] : void 0;
}
var ASANA_IMPORT_BATCH = 300;
var isoOrNull = z27.string().datetime().nullable();
var asanaImportSchema = z27.object({
  tasks: z27.array(
    z27.object({
      gid: z27.string().min(1).max(64),
      name: z27.string().trim().min(1).max(1e3),
      notes: z27.string().max(1e5),
      assigneeGid: z27.string().max(64).nullable(),
      completedAt: isoOrNull,
      dueAt: isoOrNull,
      createdAt: isoOrNull,
      projectGid: z27.string().max(64).nullable(),
      section: z27.string().trim().max(120).nullable(),
      parentGid: z27.string().max(64).nullable()
    })
  ).max(ASANA_IMPORT_BATCH),
  /** Asana project gid → CRM project id (null: no project) */
  projects: z27.record(z27.string(), z27.string().uuid().nullable()),
  /** Asana user gid → CRM user id (null: unassigned) */
  users: z27.record(z27.string(), z27.string().uuid().nullable()),
  createSections: z27.boolean().default(true)
});

// src/notes-import.ts
import { z as z28 } from "zod";
var NOTES_IMPORT_BATCH = 200;
var NOTES_IMPORT_MAX_ROWS = 1e4;
var notesImportMatchSchema = z28.object({
  emails: z28.array(z28.string().trim().toLowerCase().max(320)).max(NOTES_IMPORT_MAX_ROWS)
});
var notesImportSchema = z28.object({
  type: z28.enum(["NOTE", "CALL", "MEETING"]).default("NOTE"),
  /** move each contact's Last contacted forward to the note's date */
  updateLastContacted: z28.boolean().default(true),
  /** shown on the timeline entry, e.g. the file name */
  source: z28.string().trim().max(200).optional(),
  notes: z28.array(
    z28.object({
      email: z28.string().trim().toLowerCase().min(1).max(320),
      body: z28.string().trim().min(1).max(2e4),
      /** the last communication date; notes without one are dated now and leave Last contacted alone */
      occurredAt: z28.string().datetime({ offset: true }).nullable()
    })
  ).min(1).max(NOTES_IMPORT_BATCH)
});
var squash = (h) => h.toLowerCase().replace(/[^a-z0-9]/g, "");
var NAME_COLUMNS = /^(first|last|full)?name$|^(firstname|lastname|surname|givenname|familyname)$/;
function guessNotesColumns(headers, rows = []) {
  const find = (...tests) => {
    for (const t of tests) {
      const hit = headers.find((h) => t(squash(h)));
      if (hit) return hit;
    }
    return null;
  };
  const email = find((k) => k === "email" || k === "emailaddress", (k) => k.includes("email"));
  const note = find(
    (k) => k.includes("summary"),
    (k) => k === "note" || k === "notes" || k.includes("note"),
    (k) => k.includes("comment") || k.includes("description") || k.includes("discussed")
  );
  const date = find(
    (k) => k.includes("lastcommunication") || k.includes("lastcontact"),
    (k) => k.includes("date"),
    (k) => k.includes("when") || k.endsWith("at")
  );
  const taken = /* @__PURE__ */ new Set([email, note, date]);
  const filled = (h) => !rows.length || rows.some((r) => r[h]?.trim());
  const extras = headers.filter((h) => !taken.has(h) && !NAME_COLUMNS.test(squash(h)) && filled(h));
  return { email, note, date, extras };
}
var cellText = (v) => v === "true" ? "Yes" : v === "false" ? "No" : v;
function buildNoteBody(row, columns) {
  const main = columns.note ? (row[columns.note] ?? "").trim() : "";
  const lines = columns.extras.map((h) => [h, (row[h] ?? "").trim()]).filter(([, v]) => v).map(([h, v]) => `${h}: ${cellText(v)}`);
  return [main, lines.join("\n")].filter(Boolean).join("\n\n");
}
var MONTHS = ["jan", "feb", "mar", "apr", "may", "jun", "jul", "aug", "sep", "oct", "nov", "dec"];
function parseSheetDate(value) {
  const s = (value ?? "").trim().toLowerCase();
  if (!s) return null;
  const ok = (year, month, day) => {
    if (year < 100) year += 2e3;
    const d = new Date(Date.UTC(year, month - 1, day));
    return d.getUTCFullYear() === year && d.getUTCMonth() === month - 1 && d.getUTCDate() === day ? { year, month, day } : null;
  };
  let m = /^(\d{4})-(\d{1,2})-(\d{1,2})(?:$|[t\s])/.exec(s);
  if (m) return ok(+m[1], +m[2], +m[3]);
  m = /^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})(?:$|\s)/.exec(s);
  if (m) return +m[2] > 12 ? ok(+m[3], +m[1], +m[2]) : ok(+m[3], +m[2], +m[1]);
  m = /^(\d{1,2})\s+([a-z]{3})[a-z]*\.?,?\s+(\d{4})/.exec(s);
  if (m && MONTHS.includes(m[2])) return ok(+m[3], MONTHS.indexOf(m[2]) + 1, +m[1]);
  m = /^([a-z]{3})[a-z]*\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})/.exec(s);
  if (m && MONTHS.includes(m[1])) return ok(+m[3], MONTHS.indexOf(m[1]) + 1, +m[2]);
  return null;
}

// src/warmup.ts
import { z as z29 } from "zod";
var WARMUP_PRESETS = [
  { key: "new", label: "Brand-new domain", startVolume: 20, increasePercent: 20, description: "Never sent from before. The safest ramp." },
  { key: "history", label: "Some sending history", startVolume: 100, increasePercent: 30, description: "Has sent real email for a while, just not in bulk." },
  { key: "cold", label: "Cold outreach", startVolume: 10, increasePercent: 15, description: "Emailing people who never signed up. Go slow." }
];
var WARMUP_SUGGESTIONS = {
  startVolume: "New domain: 10\u201330. Domain that already sends some real mail: 50\u2013100.",
  increasePercent: "15\u201330% a day. Lower is safer; more than 40% defeats the purpose.",
  targetVolume: "Your normal daily volume. Warm-up ends when the limit gets here.",
  dailyLimit: "Keep a limit close to your target after warm-up, so one big campaign can\u2019t spike the volume. Empty means no limit.",
  partnerDaily: "2\u20135 per agent each weekday. They only help if the partners actually reply."
};
function warmupLimitForDay(s, day) {
  const d = Math.max(0, Math.floor(day));
  return Math.min(s.targetVolume, Math.max(1, Math.round(s.startVolume * Math.pow(1 + s.increasePercent / 100, d))));
}
function warmupDaysToTarget(s) {
  for (let d = 0; d < 3650; d++) if (warmupLimitForDay(s, d) >= s.targetVolume) return d;
  return 3650;
}
function domainDailyLimit(d, now, timeZone) {
  if (!d.warmupEnabled || !d.warmupStartedAt) return { limit: d.dailyLimit, phase: "off", day: null };
  const schedule = { startVolume: d.warmupStartVolume, increasePercent: d.warmupIncreasePercent, targetVolume: d.warmupTargetVolume };
  const day = zonedDayIndex(new Date(d.warmupStartedAt), now, timeZone);
  const ramp = warmupLimitForDay(schedule, day);
  if (ramp >= schedule.targetVolume) return { limit: d.dailyLimit, phase: "complete", day };
  return { limit: d.dailyLimit === null ? ramp : Math.min(ramp, d.dailyLimit), phase: "warming", day };
}
function safeZone(timeZone) {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone });
    return timeZone;
  } catch {
    return "UTC";
  }
}
function zonedParts(at, timeZone) {
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: safeZone(timeZone),
    hourCycle: "h23",
    year: "numeric",
    month: "numeric",
    day: "numeric",
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    weekday: "short"
  }).formatToParts(at);
  const get = (t) => parts.find((p) => p.type === t)?.value ?? "0";
  return {
    year: Number(get("year")),
    month: Number(get("month")),
    day: Number(get("day")),
    hour: Number(get("hour")) % 24,
    minute: Number(get("minute")),
    second: Number(get("second")),
    weekday: ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].indexOf(get("weekday"))
  };
}
function offsetMinutes(at, timeZone) {
  const p = zonedParts(at, timeZone);
  return Math.round((Date.UTC(p.year, p.month - 1, p.day, p.hour, p.minute, p.second) - Math.floor(at.getTime() / 1e3) * 1e3) / 6e4);
}
function startOfZonedDay(at, timeZone) {
  const p = zonedParts(at, timeZone);
  const midnightUtc = Date.UTC(p.year, p.month - 1, p.day);
  const guess = midnightUtc - offsetMinutes(at, timeZone) * 6e4;
  return new Date(midnightUtc - offsetMinutes(new Date(guess), timeZone) * 6e4);
}
function startOfNextZonedDay(at, timeZone) {
  return startOfZonedDay(new Date(startOfZonedDay(at, timeZone).getTime() + 36 * 36e5), timeZone);
}
function zonedDayIndex(from, to, timeZone) {
  return Math.round((startOfZonedDay(to, timeZone).getTime() - startOfZonedDay(from, timeZone).getTime()) / 864e5);
}
var DEFAULT_WARMUP_SETTINGS = {
  /** pause a domain's campaigns and warm-up emails when a rate goes over its limit */
  autoPause: true,
  /** percent of sent emails that bounced */
  maxBounceRate: 3,
  /** percent of sent emails reported as spam */
  maxComplaintRate: 0.1,
  /** rates are only judged once this many emails went out in the window */
  minSent: 50,
  windowDays: 7
};
var warmupSettingsSchema = z29.object({
  autoPause: z29.boolean(),
  maxBounceRate: z29.number().min(0.5).max(20),
  maxComplaintRate: z29.number().min(0.01).max(5),
  minSent: z29.number().int().min(10).max(1e5),
  windowDays: z29.number().int().min(1).max(30)
});
var SIGNAL_SUGGESTIONS = {
  maxBounceRate: "Keep bounces under 2%. Pausing at 3% stops trouble before inbox providers (or Resend) step in.",
  maxComplaintRate: "Gmail and Yahoo want spam reports under 0.3%, ideally under 0.1%. Pausing at 0.1% catches trouble early.",
  minSent: "Rates from a handful of emails jump around. 50 avoids pausing on one or two unlucky bounces.",
  windowDays: "7 days follows what Google Postmaster Tools shows."
};
function signalProblem(s, t) {
  if (s.sent < t.minSent) return null;
  if (s.complaintRate >= t.maxComplaintRate) return `Spam complaint rate ${fmtRate(s.complaintRate)} over the last ${s.windowDays} days (limit ${fmtRate(t.maxComplaintRate)})`;
  if (s.bounceRate >= t.maxBounceRate) return `Bounce rate ${fmtRate(s.bounceRate)} over the last ${s.windowDays} days (limit ${fmtRate(t.maxBounceRate)})`;
  return null;
}
function fmtRate(pct) {
  return `${pct < 1 ? pct.toFixed(2).replace(/0$/, "") : pct.toFixed(1).replace(/\.0$/, "")}%`;
}
var updateDomainWarmupSchema = z29.object({
  warmupEnabled: z29.boolean(),
  warmupStartVolume: z29.number().int().min(1).max(1e5),
  warmupIncreasePercent: z29.number().int().min(1).max(100),
  warmupTargetVolume: z29.number().int().min(1).max(1e6),
  warmupPartnerDaily: z29.number().int().min(0).max(20),
  dailyLimit: z29.number().int().min(1).max(1e6).nullable(),
  /** start the ramp over from day 1 today */
  restart: z29.boolean()
}).partial().refine((v) => v.warmupStartVolume === void 0 || v.warmupTargetVolume === void 0 || v.warmupTargetVolume >= v.warmupStartVolume, {
  message: "The target must be at least the starting volume",
  path: ["warmupTargetVolume"]
});
var EMAIL_RE3 = /^[^\s@<>,;]+@[^\s@<>,;]+\.[^\s@<>,;]+$/;
function parsePartnerList(text2) {
  const entries = /* @__PURE__ */ new Map();
  const invalid = [];
  for (const raw of splitList(text2)) {
    const item = raw.trim();
    if (!item) continue;
    const m = /^\s*"?([^"<]*?)"?\s*<([^>]+)>\s*$/.exec(item);
    const email = (m ? m[2] : item).trim().toLowerCase();
    const name = m?.[1]?.trim() || null;
    if (!EMAIL_RE3.test(email)) invalid.push(item);
    else if (!entries.has(email) || !entries.get(email) && name) entries.set(email, name);
  }
  return { entries: [...entries].map(([email, name]) => ({ email, name })), invalid };
}
function splitList(text2) {
  const out = [];
  let cur = "";
  let quoted = false;
  let angled = false;
  for (const ch of text2) {
    if (ch === '"') quoted = !quoted;
    else if (ch === "<") angled = true;
    else if (ch === ">") angled = false;
    if ((ch === "\n" || ch === "," || ch === ";") && !quoted && !angled) {
      out.push(cur);
      cur = "";
      quoted = false;
    } else cur += ch;
  }
  out.push(cur);
  return out;
}
var addWarmupPartnersSchema = z29.object({ text: z29.string().trim().min(1, "Paste at least one address").max(5e4) });
var updateWarmupPartnerSchema = z29.object({ active: z29.boolean(), name: z29.string().trim().max(120).nullable() }).partial();
var sendWarmupNowSchema = z29.object({ agentId: z29.string().uuid(), partnerId: z29.string().uuid().optional() });
var domainTestEmailSchema = z29.object({ agentId: z29.string().uuid(), to: z29.string().trim().toLowerCase().regex(EMAIL_RE3, "Enter an email address") });
function healthSummary(checks) {
  return {
    pass: checks.filter((c) => c.status === "pass").length,
    warn: checks.filter((c) => c.status === "warn").length,
    fail: checks.filter((c) => c.status === "fail").length
  };
}

// src/card-scans.ts
import { z as z30 } from "zod";
var text = (max = 300) => z30.string().trim().max(max).nullable().optional().transform((v) => v ? v : null);
var CARD_LINK_FIELDS = ["website", "linkedin", "facebook", "instagram", "x", "tiktok"];
var CARD_LINK_FIELD_KEYS = {
  website: "website",
  linkedin: "linkedin_url",
  facebook: "facebook_url",
  instagram: "instagram_url",
  x: "x_url",
  tiktok: "tiktok_url"
};
var cardAddressSchema = z30.object({
  line1: text(300),
  line2: text(300),
  city: text(120),
  state: text(120),
  postalCode: text(30),
  country: text(120)
});
var cardDetailsSchema = z30.object({
  firstName: text(120),
  lastName: text(120),
  jobTitle: text(160),
  company: text(200),
  emails: z30.array(z30.object({ email: z30.string().trim().max(200), label: text(40) })).max(10).default([]),
  phones: z30.array(z30.object({ number: z30.string().trim().max(60), label: text(40) })).max(10).default([]),
  website: text(500),
  linkedin: text(500),
  facebook: text(500),
  instagram: text(500),
  x: text(500),
  tiktok: text(500),
  address: cardAddressSchema.nullable().optional().transform((a) => a && Object.values(a).some(Boolean) ? a : null),
  /** anything else worth keeping (services, slogans, a handwritten note) */
  note: text(2e3),
  /** keys of the fields the reader was unsure about, so the review form can point them out */
  uncertain: z30.array(z30.string().max(40)).max(40).default([])
});
var emptyCardDetails = () => ({
  firstName: null,
  lastName: null,
  jobTitle: null,
  company: null,
  emails: [],
  phones: [],
  website: null,
  linkedin: null,
  facebook: null,
  instagram: null,
  x: null,
  tiktok: null,
  address: null,
  note: null,
  uncertain: []
});
var idList2 = z30.array(z30.string().uuid()).max(50);
var cardAudienceSchema = z30.object({
  tagIds: idList2.default([]),
  listIds: idList2.default([]),
  segmentIds: idList2.default([])
});
var saveCardScanSchema = cardAudienceSchema.extend({
  details: cardDetailsSchema,
  /** null = don't remember where we met */
  eventTagId: z30.string().uuid().nullable().default(null),
  existingContactId: z30.string().uuid().nullable().default(null)
});
var updateCardScanSchema = z30.object({
  eventTagId: z30.string().uuid().nullable().optional(),
  tagIds: idList2.optional(),
  listIds: idList2.optional(),
  segmentIds: idList2.optional()
});
var saveManyCardScansSchema = z30.object({ ids: z30.array(z30.string().uuid()).min(1).max(200) });
var cardMetAtLine = (where) => where?.trim() ? `Where we met: ${where.trim()}` : null;
var ADDED_BY_PARENT_TAG = "Added by";
var MET_AT_PARENT_TAG = "Met at";
var GENERIC_MAILBOXES = /* @__PURE__ */ new Set(["info", "admin", "office", "contact", "hello", "hi", "sales", "support", "team", "mail", "noreply", "no-reply", "marketing", "events", "accounts"]);
var capitalize = (s) => s ? s.charAt(0).toLocaleUpperCase() + s.slice(1).toLocaleLowerCase() : s;
function addedByTagName(email, name) {
  const local = (email.split("@")[0] ?? "").toLowerCase();
  const first = local.split(/[.+_\-\d]/).find(Boolean) ?? "";
  const fromName = (name ?? "").trim().split(/\s+/)[0] ?? "";
  return first && !GENERIC_MAILBOXES.has(local) && !GENERIC_MAILBOXES.has(first) ? capitalize(first) : fromName || capitalize(first) || "Unknown";
}
function vcardLabel(params, kind) {
  const types = params.flatMap((p) => p.replace(/^type=/i, "").split(",")).map((t) => t.toLowerCase());
  if (kind === "tel") {
    if (types.includes("cell") || types.includes("mobile")) return "Mobile";
    if (types.includes("fax")) return "Fax";
    if (types.includes("home")) return "Home";
    if (types.includes("work") || types.includes("voice")) return "Work";
    return null;
  }
  if (types.includes("home")) return "Personal";
  if (types.includes("work") || types.includes("internet")) return "Work";
  return null;
}
var unescapeVcard = (v) => v.replace(/\\n/gi, " ").replace(/\\([,;\\])/g, "$1").trim();
function sortLink(details, url) {
  const u = url.trim();
  if (!u) return;
  const host = u.replace(/^https?:\/\//i, "").toLowerCase();
  const slot = host.includes("linkedin.") ? "linkedin" : host.includes("facebook.") || host.startsWith("fb.") ? "facebook" : host.includes("instagram.") ? "instagram" : host.startsWith("x.com") || host.includes("twitter.") ? "x" : host.includes("tiktok.") ? "tiktok" : "website";
  if (!details[slot]) details[slot] = u;
}
function parseContactQr(raw) {
  const textIn = (raw ?? "").trim();
  if (!textIn) return null;
  const d = emptyCardDetails();
  if (/^BEGIN:VCARD/i.test(textIn)) {
    const lines = textIn.replace(/\r?\n[ \t]/g, "").split(/\r?\n/);
    let fullName = null;
    for (const line of lines) {
      const colon = line.indexOf(":");
      if (colon < 0) continue;
      const [rawKey, ...params] = line.slice(0, colon).split(";");
      const key = rawKey.replace(/^item\d+\./i, "").toUpperCase();
      const value = line.slice(colon + 1);
      if (key === "N") {
        const [last, first] = value.split(";").map(unescapeVcard);
        d.lastName = last || null;
        d.firstName = first || null;
      } else if (key === "FN") fullName = unescapeVcard(value) || null;
      else if (key === "ORG") d.company = unescapeVcard(value.split(";")[0]) || null;
      else if (key === "TITLE") d.jobTitle = unescapeVcard(value) || null;
      else if (key === "EMAIL" && value.trim()) d.emails.push({ email: unescapeVcard(value), label: vcardLabel(params, "email") });
      else if (key === "TEL" && value.trim()) d.phones.push({ number: unescapeVcard(value.replace(/^tel:/i, "")), label: vcardLabel(params, "tel") });
      else if (key === "URL") sortLink(d, unescapeVcard(value));
      else if (key === "NOTE") d.note = unescapeVcard(value) || null;
      else if (key === "ADR") {
        const [, extended, street, city, state, postalCode, country] = value.split(";").map(unescapeVcard);
        const address = { line1: street || null, line2: extended || null, city: city || null, state: state || null, postalCode: postalCode || null, country: country || null };
        if (!d.address && Object.values(address).some(Boolean)) d.address = address;
      }
    }
    if (!d.firstName && !d.lastName && fullName) {
      const parts = fullName.split(/\s+/);
      d.firstName = parts[0] ?? null;
      d.lastName = parts.slice(1).join(" ") || null;
    }
    return d;
  }
  if (/^MECARD:/i.test(textIn)) {
    for (const part2 of textIn.slice(7).split(/(?<!\\);/)) {
      const colon = part2.indexOf(":");
      if (colon < 0) continue;
      const key = part2.slice(0, colon).toUpperCase();
      const value = unescapeVcard(part2.slice(colon + 1));
      if (!value) continue;
      if (key === "N") {
        const [last, first] = value.split(",").map((s) => s.trim());
        d.lastName = last || null;
        d.firstName = first || null;
      } else if (key === "TEL") d.phones.push({ number: value, label: null });
      else if (key === "EMAIL") d.emails.push({ email: value, label: null });
      else if (key === "URL") sortLink(d, value);
      else if (key === "ORG") d.company = value;
      else if (key === "NOTE") d.note = value;
      else if (key === "ADR") d.address = { line1: value, line2: null, city: null, state: null, postalCode: null, country: null };
    }
    return d;
  }
  return null;
}
var digits4 = (s) => s.replace(/\D/g, "");
function mergeCardDetails(read, qr) {
  if (!qr) return read;
  const emails = [...qr.emails];
  for (const e of read.emails) if (!emails.some((x) => x.email.toLowerCase() === e.email.toLowerCase())) emails.push(e);
  const phones = [...qr.phones];
  for (const p of read.phones) if (!phones.some((x) => digits4(x.number).endsWith(digits4(p.number).slice(-8)))) phones.push(p);
  const pick = (k) => qr[k] ?? read[k];
  const fromQr = Object.keys(qr).filter((k) => {
    const v = qr[k];
    return Array.isArray(v) ? v.length > 0 : !!v;
  });
  return {
    firstName: pick("firstName"),
    lastName: pick("lastName"),
    jobTitle: pick("jobTitle"),
    company: pick("company"),
    emails,
    phones,
    website: pick("website"),
    linkedin: pick("linkedin"),
    facebook: pick("facebook"),
    instagram: pick("instagram"),
    x: pick("x"),
    tiktok: pick("tiktok"),
    address: qr.address ?? read.address,
    note: [read.note, qr.note].filter(Boolean).join("\n") || null,
    uncertain: read.uncertain.filter((k) => !fromQr.includes(k))
  };
}

// src/dashboard.ts
import { z as z31 } from "zod";
var DASHBOARD_PERIODS = ["week", "month"];
var dashboardQuerySchema = z31.object({ period: z31.enum(DASHBOARD_PERIODS).default("week") });
function zoneOffset(at, timeZone) {
  const parts = Object.fromEntries(
    new Intl.DateTimeFormat("en-US", { timeZone, hourCycle: "h23", year: "numeric", month: "numeric", day: "numeric", hour: "numeric", minute: "numeric", second: "numeric" }).formatToParts(at).filter((p) => p.type !== "literal").map((p) => [p.type, Number(p.value)])
  );
  return Date.UTC(parts.year, parts.month - 1, parts.day, parts.hour, parts.minute, parts.second) - Math.floor(at.getTime() / 1e3) * 1e3;
}
function midnight(year, month, day, timeZone) {
  const guess = Date.UTC(year, month, day);
  const first = guess - zoneOffset(new Date(guess), timeZone);
  return new Date(guess - zoneOffset(new Date(first), timeZone));
}
function periodBounds(period, timeZone, now = /* @__PURE__ */ new Date()) {
  let zone = timeZone || "UTC";
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
  } catch {
    zone = "UTC";
  }
  const local = new Date(now.getTime() + zoneOffset(now, zone));
  const [y, m, d] = [local.getUTCFullYear(), local.getUTCMonth(), local.getUTCDate()];
  if (period === "month") return { from: midnight(y, m, 1, zone), to: now, previousFrom: midnight(y, m - 1, 1, zone) };
  const sinceMonday = (local.getUTCDay() + 6) % 7;
  return { from: midnight(y, m, d - sinceMonday, zone), to: now, previousFrom: midnight(y, m, d - sinceMonday - 7, zone) };
}

// src/sequences.ts
import { z as z32 } from "zod";
var SEQUENCE_STATUSES = ["DRAFT", "ACTIVE", "PAUSED", "ARCHIVED"];
var ENROLLMENT_STATUSES = ["ACTIVE", "PAUSED", "COMPLETED", "REPLIED", "BOUNCED", "UNSUBSCRIBED", "FAILED", "REMOVED"];
var STEP_TYPES = ["EMAIL", "TASK", "ACTION"];
var STEP_CONDITIONS = ["always", "not_opened", "opened", "not_clicked", "clicked"];
var STEP_CONDITION_LABELS = {
  always: "Always",
  not_opened: "Only if the previous email was not opened",
  opened: "Only if the previous email was opened",
  not_clicked: "Only if no link in the previous email was clicked",
  clicked: "Only if a link in the previous email was clicked"
};
var id2 = z32.string().uuid();
var sequenceActionSchema = z32.discriminatedUnion("type", [
  z32.object({ type: z32.literal("add_tag"), tagId: id2 }),
  z32.object({ type: z32.literal("remove_tag"), tagId: id2 }),
  z32.object({ type: z32.literal("add_to_list"), listId: id2 }),
  z32.object({ type: z32.literal("remove_from_list"), listId: id2 }),
  z32.object({ type: z32.literal("set_lead_status"), value: z32.string().trim().max(120) }),
  z32.object({ type: z32.literal("set_lifecycle_stage"), value: z32.string().trim().max(120) }),
  z32.object({
    type: z32.literal("create_task"),
    title: z32.string().trim().min(1).max(300),
    note: z32.string().max(5e3).nullable().optional(),
    dueInDays: z32.number().int().min(0).max(365).default(0),
    priority: z32.enum(["LOW", "MEDIUM", "HIGH", "URGENT"]).nullable().optional(),
    assigneeUserId: id2.nullable().optional()
  })
]);
var SEQUENCE_ACTION_LABELS = {
  add_tag: "Add tag",
  remove_tag: "Remove tag",
  add_to_list: "Add to list",
  remove_from_list: "Remove from list",
  set_lead_status: "Set lead status",
  set_lifecycle_stage: "Set lifecycle stage",
  create_task: "Create task"
};
var forwardSchema = z32.object({
  enabled: z32.boolean().default(false),
  fromName: z32.string().trim().max(200).default(""),
  fromEmail: z32.string().trim().max(320).default(""),
  toName: z32.string().trim().max(200).default(""),
  toEmail: z32.string().trim().max(320).default(""),
  cc: z32.string().trim().max(1e3).default(""),
  /** relative: N days before the forward is sent (at a plausible working-hours time); fixed: the given date */
  dateMode: z32.enum(["relative", "fixed"]).default("relative"),
  daysBefore: z32.number().int().min(0).max(365).default(3),
  date: z32.string().datetime({ offset: true }).nullable().optional(),
  subject: z32.string().trim().max(500).default(""),
  html: z32.string().max(2e5).default("")
});
var emptyForward = () => forwardSchema.parse({});
var sequenceStepSchema = z32.object({
  /** existing step id, so a saved step keeps its stats */
  id: z32.string().uuid().optional(),
  type: z32.enum(STEP_TYPES),
  /** wait after the previous step (or after enrolment, for the first step) */
  delayDays: z32.number().int().min(0).max(365).default(0),
  delayHours: z32.number().int().min(0).max(23).default(0),
  condition: z32.enum(STEP_CONDITIONS).default("always"),
  // EMAIL
  /** reply: same thread, "Re: <first subject>"; new: a new email with its own subject */
  threadMode: z32.enum(["reply", "new"]).default("new"),
  subject: z32.string().max(500).default(""),
  html: z32.string().max(2e5).default(""),
  includeSignature: z32.boolean().default(true),
  /** follow-ups in the same thread quote the previous email below ("On …, <agent> wrote:"), as a mail client does */
  quotePrevious: z32.boolean().default(true),
  forward: forwardSchema.optional(),
  // TASK / ACTION
  actions: z32.array(sequenceActionSchema).max(20).default([])
});
var saveStepsSchema = z32.object({ steps: z32.array(sequenceStepSchema).max(30) }).superRefine((v, ctx) => {
  v.steps.forEach((s, i) => {
    if (s.type === "EMAIL") {
      const firstEmail = v.steps.findIndex((x) => x.type === "EMAIL") === i;
      if (!htmlHasText(s.html) && !s.forward?.enabled) ctx.addIssue({ code: "custom", message: `Step ${i + 1}: write the email`, path: ["steps", i, "html"] });
      const needsSubject = firstEmail || s.threadMode === "new";
      if (needsSubject && !s.subject.trim() && !(s.forward?.enabled && s.forward.subject.trim())) ctx.addIssue({ code: "custom", message: `Step ${i + 1}: add a subject`, path: ["steps", i, "subject"] });
    }
    if (s.type !== "EMAIL" && !s.actions.length) ctx.addIssue({ code: "custom", message: `Step ${i + 1}: add at least one action`, path: ["steps", i, "actions"] });
  });
});
var hhmm = z32.string().regex(/^([01]\d|2[0-3]):[0-5]\d$/, "Use HH:MM");
var sequenceSettingsSchema = z32.object({
  /** IANA zone for the send window and the daily counts; null = the workspace time zone */
  timezone: z32.string().max(64).nullable().default(null),
  /** 0 = Sunday … 6 = Saturday */
  sendDays: z32.array(z32.number().int().min(0).max(6)).max(7).default([1, 2, 3, 4, 5]),
  sendFrom: hhmm.default("08:00"),
  sendTo: hhmm.default("17:00"),
  /** people who get their first email per day */
  newPerDay: z32.number().int().min(1).max(5e3).default(30),
  /** all emails from this sequence per day (first emails and follow-ups) */
  maxPerDay: z32.number().int().min(1).max(2e4).default(150),
  /** emails per agent mailbox per day, over all sequences */
  perAgentPerDay: z32.number().int().min(1).max(5e3).default(50),
  /** random pause between two emails from the same agent, so sends don't come in bursts */
  minGapMinutes: z32.number().int().min(1).max(240).default(2),
  maxGapMinutes: z32.number().int().min(1).max(240).default(6),
  /** people who newly match the audience join on their own */
  autoEnroll: z32.boolean().default(false),
  /** leave out people who are active in another sequence */
  skipActiveElsewhere: z32.boolean().default(true),
  requireVerified: z32.boolean().default(false),
  includeUnconfirmed: z32.boolean().default(true),
  trackOpens: z32.boolean().default(true),
  trackClicks: z32.boolean().default(true),
  /** a click finishes the sequence for that person */
  stopOnClick: z32.boolean().default(false),
  /** a reply from anyone at the same organization finishes the sequence for their colleagues */
  stopOnOrgReply: z32.boolean().default(false),
  /** out-of-office and other automatic replies don't stop the sequence */
  ignoreAutoReplies: z32.boolean().default(true),
  /** threads show in the inbox from the first email; off = only once the person replies */
  showInInboxBeforeReply: z32.boolean().default(false),
  /** List-Unsubscribe header (one-click unsubscribe in Gmail / Yahoo); makes the email look less like a personal one */
  listUnsubscribeHeader: z32.boolean().default(false),
  onReply: z32.array(sequenceActionSchema).max(20).default([]),
  onFinish: z32.array(sequenceActionSchema).max(20).default([])
});
var DEFAULT_SEQUENCE_SETTINGS = sequenceSettingsSchema.parse({});
var parseSequenceSettings = (raw) => {
  const r = sequenceSettingsSchema.safeParse(raw ?? {});
  return r.success ? r.data : DEFAULT_SEQUENCE_SETTINGS;
};
var createSequenceSchema = z32.object({
  name: z32.string().trim().min(1).max(160),
  projectId: projectLabelSchema,
  audience: audienceSchema.optional(),
  agentIds: z32.array(z32.string().uuid()).max(50).optional(),
  settings: sequenceSettingsSchema.partial().optional()
});
var updateSequenceSchema = createSequenceSchema.partial();
var enrollContactsSchema = z32.object({ contactIds: z32.array(z32.string().uuid()).min(1).max(1e4) });
var enrollmentActionSchema = z32.object({ ids: z32.array(z32.string().uuid()).min(1).max(5e3), action: z32.enum(["pause", "resume", "remove", "restart"]) });
var listEnrollmentsSchema = z32.object({
  status: z32.enum(["all", ...ENROLLMENT_STATUSES]).default("all"),
  q: z32.string().trim().max(200).optional(),
  page: z32.coerce.number().int().min(1).default(1),
  pageSize: z32.coerce.number().int().min(1).max(200).default(50)
});
var previewStepSchema = z32.object({ step: sequenceStepSchema, contactId: z32.string().uuid().nullable().optional(), agentId: z32.string().uuid().nullable().optional() });
var testStepSchema = previewStepSchema.extend({ to: z32.string().trim().toLowerCase().email() });
var htmlHasText = (html) => html.replace(/<[^>]+>/g, "").replace(/&nbsp;/g, " ").trim().length > 0;
function humanizeHtml(html) {
  const body = html.replace(/\s(class|style|rel|target|data-[\w-]+)="[^"]*"/g, "").replace(/<p>\s*<\/p>/g, "<div><br></div>").replace(/<p>/g, "<div>").replace(/<\/p>/g, "</div>");
  return `<div dir="ltr">${body}</div>`;
}
var SPIN_RE = /(?<!\{)\{([^{}]*\|[^{}]*)\}(?!\})/g;
function applySpintax(text2, random = Math.random) {
  return text2.replace(SPIN_RE, (_m, body) => {
    const options = body.split("|");
    return options[Math.floor(random() * options.length)] ?? "";
  });
}
var WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
function forwardDate(at, timeZone) {
  const p = zonedParts(at, timeZone);
  const month = new Intl.DateTimeFormat("en-US", { month: "short", timeZone: "UTC" }).format(new Date(Date.UTC(2e3, p.month - 1, 1)));
  const h12 = p.hour % 12 === 0 ? 12 : p.hour % 12;
  return `${WEEKDAYS[p.weekday]}, ${month} ${p.day}, ${p.year} at ${h12}:${String(p.minute).padStart(2, "0")} ${p.hour < 12 ? "AM" : "PM"}`;
}
function forwardedAt(f, sendAt, timeZone, seed = "x") {
  if (f.dateMode === "fixed" && f.date) return new Date(f.date);
  let h = 0;
  for (const ch of seed) h = h * 31 + ch.charCodeAt(0) >>> 0;
  const minuteOfDay = 9 * 60 + h % (8 * 60);
  const day = startOfZonedDay(new Date(sendAt.getTime() - f.daysBefore * 864e5), timeZone);
  return new Date(Math.min(day.getTime() + minuteOfDay * 6e4, sendAt.getTime() - 37 * 6e4));
}
var esc2 = (s) => s.replace(/[&<>"]/g, (ch) => ({ "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;" })[ch]);
function forwardBlockHtml(f, dateText) {
  const person = (name, email) => name && email ? `<strong class="gmail_sendername" dir="auto">${esc2(name)}</strong> <span dir="auto">&lt;<a href="mailto:${esc2(email)}">${esc2(email)}</a>&gt;</span>` : name ? `<strong class="gmail_sendername" dir="auto">${esc2(name)}</strong>` : `<span dir="auto">&lt;<a href="mailto:${esc2(email)}">${esc2(email)}</a>&gt;</span>`;
  const to = f.toName || f.toEmail ? `To: ${f.toName ? `${esc2(f.toName)} ` : ""}${f.toEmail ? `&lt;<a href="mailto:${esc2(f.toEmail)}">${esc2(f.toEmail)}</a>&gt;` : ""}<br>` : "";
  const cc = f.cc ? `Cc: ${esc2(f.cc)}<br>` : "";
  return `<div class="gmail_quote"><div dir="ltr" class="gmail_attr">---------- Forwarded message ---------<br>From: ${person(f.fromName, f.fromEmail)}<br>Date: ${esc2(dateText)}<br>Subject: ${esc2(f.subject)}<br>${to}${cc}</div><br><br>${humanizeHtml(f.html)}</div>`;
}
var forwardSubject = (f) => `Fwd: ${f.subject}`;
var replySubject = (subject) => /^\s*re\s*:/i.test(subject) ? subject : `Re: ${subject.replace(/^\s*(fwd?|fw)\s*:\s*/i, "")}`;
var minutesOf = (hhmmValue) => {
  const [h, m] = hhmmValue.split(":").map(Number);
  return h * 60 + m;
};
function inSendWindow(now, s, timeZone) {
  const p = zonedParts(now, timeZone);
  if (!s.sendDays.includes(p.weekday)) return false;
  const minute = p.hour * 60 + p.minute;
  return minute >= minutesOf(s.sendFrom) && minute < minutesOf(s.sendTo);
}
var stepDelayMinutes = (s) => s.delayDays * 1440 + s.delayHours * 60;
function stepDays(steps) {
  let total = 0;
  return steps.map((s) => {
    total += stepDelayMinutes(s);
    return Math.floor(total / 1440) + 1;
  });
}
function isAutoReply(subject, text2) {
  const re = /\b(out of (the )?office|automatic reply|auto[- ]?reply|autoreply|auto[- ]?response|away from (the )?office|on vacation|on leave|abwesenheit|absence|delivery status notification|undeliverable)\b/i;
  return re.test(subject) || /^\s*(automatic reply|auto:)/i.test(subject) || re.test((text2 ?? "").slice(0, 300));
}
function randomGapMinutes(s, random = Math.random) {
  const lo = Math.min(s.minGapMinutes, s.maxGapMinutes);
  const hi = Math.max(s.minGapMinutes, s.maxGapMinutes);
  return lo + random() * (hi - lo);
}
function quotedReplyHtml(previousHtml, header) {
  return `<div class="gmail_quote"><div dir="ltr" class="gmail_attr">${esc2(header)}<br></div><blockquote class="gmail_quote" style="margin:0px 0px 0px 0.8ex;border-left:1px solid rgb(204,204,204);padding-left:1ex">${previousHtml}</blockquote></div>`;
}
var quoteHeader = (at, timeZone, name, email) => `On ${forwardDate(at, timeZone)} ${name} <${email}> wrote:`;
export {
  ACTIVITY_TYPES,
  ADDED_BY_PARENT_TAG,
  ADDRESS_LABELS,
  ADDRESS_PARTS,
  ADDRESS_PART_LABELS,
  ADDRESS_TARGETS,
  AI_LENGTHS,
  AI_PROVIDERS,
  AI_PROVIDER_LABELS,
  AI_REVISE_SUGGESTIONS,
  AI_TONES,
  ASANA_DEFAULT_SECTION,
  ASANA_IMPORT_BATCH,
  BLOCK_LABELS,
  CAMPAIGN_STATUSES,
  CARD_LINK_FIELDS,
  CARD_LINK_FIELD_KEYS,
  CLEANUP_CASES,
  CLEANUP_FIELDS,
  CLEANUP_FIELD_LABELS,
  CONTACT_ISSUES,
  CONTACT_ISSUE_CODES,
  CONTACT_PSEUDO_FIELDS,
  DASHBOARD_PERIODS,
  DEFAULT_EMAIL_STYLE,
  DEFAULT_KEEP_WORDS,
  DEFAULT_SEND_STATUSES,
  DEFAULT_SEQUENCE_SETTINGS,
  DEFAULT_WARMUP_SETTINGS,
  EMAIL_BANNER,
  EMAIL_FONTS,
  EMAIL_LABELS,
  EMAIL_STATUSES,
  ENROLLMENT_STATUSES,
  FIELD_TYPES,
  IMPORT_CONTACT_GROUP_TARGETS,
  IMPORT_CONTACT_ITEM_TARGETS,
  IMPORT_CONTACT_MULTI_TARGETS,
  IMPORT_CONTACT_ORGANIZATION_TARGETS,
  IMPORT_DUPLICATE_MODES,
  IMPORT_LOGO_LABEL,
  IMPORT_MAX_SELECT_OPTIONS,
  IMPORT_NEW_FIELD,
  IMPORT_NEW_FIELD_TYPES,
  IMPORT_NEW_FIELD_TYPE_LABELS,
  IMPORT_NOTES,
  IMPORT_PERSON_KEYS,
  IMPORT_REPEATABLE_TARGETS,
  IMPORT_SEGMENTS,
  IMPORT_SPECIAL_TARGETS,
  LIST_FIELD_TYPES,
  MARK_INVALID_BATCH,
  MERGEABLE_SYSTEM_KEYS,
  MERGE_SELECTED_TIER,
  MERGE_TAGS,
  MET_AT_PARENT_TAG,
  MOBILE_LABEL,
  NOTES_IMPORT_BATCH,
  NOTES_IMPORT_MAX_ROWS,
  NO_SIGNATURE,
  OBJECT_TYPES,
  ORGANIZATION_FIELD_PREFIX,
  ORGANIZATION_MATCH_KEYS,
  ORG_FIELD_PREFIX,
  PHONE_LABELS,
  PROJECTS_MIN_ACTIVE,
  PROJECT_COLOR_KEYS,
  PROJECT_HEADER,
  REASON_LABELS,
  REMOVABLE_STATUSES,
  REMOVE_BAD_EMAILS_BATCH,
  REPORTS_GRACE_MS,
  RESEND_SYNC_QUEUED_STALE_MS,
  RESEND_SYNC_STALE_MS,
  RULE_OPERATORS,
  SENDABLE_STATUSES,
  SEQUENCE_ACTION_LABELS,
  SEQUENCE_STATUSES,
  SIGNAL_SUGGESTIONS,
  SIGNATURE_SLOT,
  SKIP_REASONS,
  STEP_CONDITIONS,
  STEP_CONDITION_LABELS,
  STEP_TYPES,
  TASK_PRIORITIES,
  UNCONFIRMED_STATUSES,
  USER_ROLES,
  USER_STATUSES,
  VERIFY_VENDORS,
  WARMUP_PRESETS,
  WARMUP_SUGGESTIONS,
  WEEKDAYS,
  acceptInviteSchema,
  addAddress,
  addWarmupPartnersSchema,
  addedByTagName,
  addressCandidateKey,
  addressCoveredBy,
  addressListSchema,
  addressMergerApplySchema,
  addressMergerScanSchema,
  addressSchema,
  aiEmailContentSchema,
  aiReviseEmailSchema,
  aiSettingsSchema,
  aiWriteEmailSchema,
  applyCountryCodes,
  applySpintax,
  asanaImportSchema,
  audiencePeopleSchema,
  audienceSchema,
  blockPaddingSchema,
  buildNoteBody,
  bulkDeleteSchema,
  bulkDeleteTasksSchema,
  bulkIdsSchema,
  bulkListsSchema,
  bulkSegmentsSchema,
  bulkSelectionSchema,
  bulkTagsSchema,
  bulkUpdateSchema,
  bulkUpdateTasksSchema,
  callingCodeOf,
  campaignReportsMissing,
  cardAddressSchema,
  cardAudienceSchema,
  cardDetailsSchema,
  cardMetAtLine,
  changeFieldTypeSchema,
  changePasswordSchema,
  channelCleanerApplySchema,
  channelCleanerScanSchema,
  channelFindingKey,
  checkContact,
  classifyAddressField,
  cleanName,
  cleanupFieldOptionsSchema,
  conditionSchema,
  contactEmailItemSchema,
  contactPhoneItemSchema,
  contactSystemFieldsSchema,
  countryCodeFindingKey,
  countryCodeFix,
  countryCodesApplySchema,
  countryCodesScanSchema,
  createAgentSchema,
  createCampaignSchema,
  createContactSchema,
  createDealSchema,
  createDomainSchema,
  createFieldSchema,
  createFormSchema,
  createListSchema,
  createNoteSchema,
  createOrganizationSchema,
  createPipelineSchema,
  createProjectSchema,
  createSegmentSchema,
  createSequenceSchema,
  createStageSchema,
  createTagSchema,
  createTaskSchema,
  createTaskSectionSchema,
  createTemplateSchema,
  customFieldsSchema,
  dashboardQuerySchema,
  dealConditionSchema,
  dealSystemFieldsSchema,
  dedupePhoneList,
  defaultBlockPadding,
  defaultEmailDesign,
  deleteStageSchema,
  describeAddresses,
  designFromHtml,
  detectContactCountry,
  dismissByReasonsSchema,
  dismissCandidatesSchema,
  domainDailyLimit,
  domainTestEmailSchema,
  duplicatePhonesApplySchema,
  duplicatePhonesScanSchema,
  emailBlockSchema,
  emailDesignProblems,
  emailDesignSchema,
  emailStyleSchema,
  emptyAudience,
  emptyCardDetails,
  emptyForward,
  emptyRuleGroup,
  engagementConditionSchema,
  enrollContactsSchema,
  enrollmentActionSchema,
  fieldConditionSchema,
  fieldKeyRegex,
  fieldOptionSchema,
  findContactChannels,
  findCountryCodeFixes,
  findDuplicatePhones,
  findEmails,
  findMergeTags,
  findPhones,
  findRecordAddresses,
  fmtRate,
  forgotPasswordSchema,
  formFieldSchema,
  formProject,
  formSettingsSchema,
  formTheme,
  formatAddress,
  forwardBlockHtml,
  forwardDate,
  forwardSchema,
  forwardSubject,
  forwardedAt,
  guessNotesColumns,
  healthSummary,
  htmlHasText,
  humanizeHtml,
  importColumnGroup,
  importMappingSchema,
  importTargetLabel,
  inSendWindow,
  inviteUserSchema,
  isAutoReply,
  isDuplicatePhone,
  isEmptyAddress,
  isImportGroupTarget,
  isPhoneFieldName,
  isUsableLink,
  labelBefore,
  listActivitiesSchema,
  listConditionSchema,
  listDealsSchema,
  listDuplicatesSchema,
  listEnrollmentsSchema,
  listImportRowsSchema,
  listMembersSchema,
  listQuerySchema,
  listRecipientsSchema,
  listSubmissionsSchema,
  listTasksSchema,
  listThreadsSchema,
  loginSchema,
  markInvalidSchema,
  massMergeOptionsSchema,
  matchAsanaUser,
  matchCountry,
  mergeCandidateSchema,
  mergeCardDetails,
  mergeChoicesSchema,
  mergeFieldSchema,
  mergeRecordsSchema,
  mergeSegmentsSchema,
  mergeSelectedSchema,
  mergeValues,
  moveDealSchema,
  nameCleanupApplySchema,
  nameCleanupSchema,
  newBlockId,
  newEmailBlock,
  noContactInfoFieldsSchema,
  noContactInfoQuerySchema,
  normalizeAddress,
  normalizeAddressList,
  notesImportMatchSchema,
  notesImportSchema,
  oddContactsScanSchema,
  organizationConditionSchema,
  organizationSystemFieldsSchema,
  paginationSchema,
  parseAddress,
  parseAsanaExport,
  parseContactQr,
  parseEmailList,
  parseModelRef,
  parsePartnerList,
  parseSequenceSettings,
  parseSheetDate,
  periodBounds,
  phoneCountries,
  previewAudienceSchema,
  previewSegmentSchema,
  previewStepSchema,
  projectConditionSchema,
  projectEmailStyle,
  projectLabelSchema,
  queryTasksSchema,
  quoteHeader,
  quotedReplyHtml,
  randomGapMinutes,
  readableOn,
  registerSchema,
  removeBadEmailsSchema,
  removeValues,
  renderEmailDesign,
  renderMergeTags,
  reorderFieldsSchema,
  reorderStagesSchema,
  reorderTaskSectionsSchema,
  reorderTasksSchema,
  replySubject,
  replyThreadSchema,
  rerunImportSchema,
  resendSyncActive,
  resetDuplicateScanSchema,
  resetPasswordSchema,
  resolveEmailsSchema,
  ruleGroupSchema,
  sameAddress,
  samePhoneNumber,
  saveCardScanSchema,
  saveManyCardScansSchema,
  saveStepsSchema,
  segmentConditionSchema,
  sendCampaignSchema,
  sendEmailSchema,
  sendWarmupNowSchema,
  sequenceActionSchema,
  sequenceSettingsSchema,
  sequenceStepSchema,
  setContactTagsSchema,
  signalProblem,
  startMassMergeSchema,
  startOfNextZonedDay,
  startOfZonedDay,
  stepDays,
  stepDelayMinutes,
  suggestChannelLabel,
  suggestColumnLabel,
  tagConditionSchema,
  taskActivityQuerySchema,
  taskCommentSchema,
  testSendSchema,
  testStepSchema,
  updateAgentSchema,
  updateCampaignSchema,
  updateCardScanSchema,
  updateContactSchema,
  updateDealSchema,
  updateDomainSchema,
  updateDomainWarmupSchema,
  updateFieldSchema,
  updateFormSchema,
  updateListSchema,
  updateOrganizationSchema,
  updatePipelineSchema,
  updateProfileSchema,
  updateProjectSchema,
  updateSegmentSchema,
  updateSequenceSchema,
  updateStageSchema,
  updateTagSchema,
  updateTaskSchema,
  updateTaskSectionSchema,
  updateTemplateSchema,
  updateThreadSchema,
  updateUserSchema,
  updateWarmupPartnerSchema,
  verifyContactsSchema,
  verifySingleSchema,
  warmupDaysToTarget,
  warmupLimitForDay,
  warmupSettingsSchema,
  zonedDayIndex,
  zonedParts
};
//# sourceMappingURL=index.mjs.map